/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
/* eslint-disable eqeqeq */
import React from 'react';
import { find, filter, cloneDeep, findIndex, compact, flatten, uniq } from 'lodash';
import moment from 'moment-timezone';

import { arraysEqual, getRatingDetails, setRatingChoice } from './index';
import { acceptanceValues, acceptanceRankingValues, EventVerifiedTypes, EventHsTypes, drawPositionTypeIds, drawCategoryTypeIds, drawEliminationTypeIds, utrTypeIds, ballTypeIds, ballTypes, directorRoleIds, clubMemberRoleIds, EventScheduleTypes, divisionFormatTypeIds, sportTypeIds, drawEliminationTypes, matchCounts, eventGenderTypeIds, registrationStatusTypeIds, registrationGroupStatusTypeIds, paymentStatusTypeIds, teamPaymentTypeIds, noMaximumLabelValue, registrationStateTypeIds, LeagueGenderValues, eventQuestionTypeIds, scoringTypeOverrides, verifiedAcceptanceMinValue, divisionRegistrationTypeIds, eventStateTypeIds, acceptanceBucketTypeSortKey } from './eventConstants';
import { eventDivisionAgeSliderDictionary, getEventDivisionAgeKeyByValue, sortParamTypes, RestrictAgeByDateTypeIds, ageDisplay, ratingTypes } from './constants';

import config from '../../config';
import { getEventDateString } from './formatting';
import { importedEventTypes, canBuildImportedEventPath, buildImportedEventPath } from '../pages/ImportedEvents/importedEventPaths';
import { isActive as isClubActive, canRunEvents as clubCanRunEvents, isHighSchoolClub } from './clubs';
import EventTours from './EventTours';
import { pickleballRatingDisplayV2 } from '../pages/NuoV2/YourRating/constants';
import { getLabelByTypeId } from './types';
import { SortType } from '../components/ColumnHeader';
import * as checkoutEventRegistrationApi from '../api/checkoutEventRegistrationApi';

const _ = { find, filter, cloneDeep, findIndex, compact, flatten, uniq };

export const EventVerificationTypes = {
    VERIFIED: '2',
    UNVERIFIED: '1',
};

export const DEFAULT_MAX_VERIFIED_EVENT_DURATION = 14;

export const EventTypes = {
    TOURNAMENT: 1,
    LEAGUE_OR_TEAM: 2,
    GENERAL_EVENT: 3,
    FREE_PLAY: 4, // also called GROUP_PLAY
    CAMP: 5,
    CLINIC: 6,
    MATCH_PLAY: 7,
    DUAL_MATCH: 8,
    NON_TENNIS: 9, // obsolete
    VIRTUAL: 10, // obsolete
    FLEX_LEAGUE: 11,
    TEAM_LEAGUE: 12, // UTR Team Tennis, obsolete
    TEAM_MATCH: 13, // Adult Team Leagues
    PAID_HIT: 14,
};

export const ImportedEventSources = {
    TOURNAMENT: 'imported-tournament',
    LEAGUE: 'imported-league',
};

export const LeagueSessionSource = 'league-session';

export const isImportedProfileEvent = (event) => {
    return event?.source === ImportedEventSources.TOURNAMENT || event?.source === ImportedEventSources.LEAGUE;
};

export const isLeagueSessionProfileEvent = (event) => {
    return event?.source === LeagueSessionSource;
};

export const isRenderableProfileEvent = (event, isImportedEventPagesEnabled) => {
    if (!isImportedProfileEvent(event)) {
        return true;
    }

    return isImportedEventPagesEnabled &&
        !!event.importedPageReady &&
        canBuildImportedEventPath({ importedEventType: event.importedEventType, eventId: event.id });
};

export const filterRenderableProfileEvents = (events, isImportedEventPagesEnabled) => {
    return events?.length > 0
        ? events.filter(event => isRenderableProfileEvent(event, isImportedEventPagesEnabled))
        : [];
};

const ImportedEventSourceByType = {
    [importedEventTypes.TOURNAMENT]: ImportedEventSources.TOURNAMENT,
    [importedEventTypes.LEAGUE]: ImportedEventSources.LEAGUE,
};

const ImportedEventTypeBySource = Object.entries(ImportedEventSourceByType)
    .reduce((typesBySource, [type, source]) => {
        typesBySource[source] = type;
        return typesBySource;
    }, {});

export const EventInviteStatusId = {
    ACCEPTED: 1,
    DECLINED: 2,
    INVITED: 3,
};

export const EventInviteStatus = {
    ACCEPTED: 'Accepted',
    DECLINED: 'Declined',
    INVITED: 'Invited',
};

export const EventDivisionCategory = {
    PRO: 1,
    AMATEUR: 2,
};
export const PaidHitEventSelfWithdrawBeforeHours = 48;

export function doesResultAlignWithMatch(match, playersDict, teamsDict, playersDictByMemberId) {
    const { result } = match;
    if (!result) {
        return true;
    }
    let team1 = [];
    let team2 = [];
    // v1
    if (match.position1 && match.position1.type?.value === 'player' && match.position1.id && playersDict[match.position1.id]) {
        team1.push(playersDict[match.position1.id].id);
    }
    if (match.position2 && match.position2.type?.value === 'player' && match.position2.id && playersDict[match.position2.id]) {
        team2.push(playersDict[match.position2.id].id);
    }
    if (match.position1 && match.position1.type?.value === 'team' && match.position1.id && teamsDict[match.position1.id]) {
        team1 = teamsDict[match.position1.id].players;
    }
    if (match.position2 && match.position2.type?.value === 'team' && match.position2.id && teamsDict[match.position2.id]) {
        team2 = teamsDict[match.position2.id].players;
    }
    // v2
    if (match.position1?.typeId === drawPositionTypeIds.PLAYER && match.position1.memberId && playersDictByMemberId[match.position1.memberId]) {
        team1.push(playersDictByMemberId[match.position1.memberId].playerId);
    }
    if (match.position2?.typeId === drawPositionTypeIds.PLAYER && match.position2.memberId && playersDictByMemberId[match.position2.memberId]) {
        team2.push(playersDictByMemberId[match.position2.memberId].playerId);
    }
    if (match.position1?.typeId === drawPositionTypeIds.TEAM && match.position1.teamId && teamsDict[match.position1.teamId]) {
        team1 = [playersDictByMemberId[teamsDict[match.position1.teamId].members[0]].playerId, playersDictByMemberId[teamsDict[match.position1.teamId].members[1]].playerId];
    }
    if (match.position2?.typeId === drawPositionTypeIds.TEAM && match.position2.teamId && teamsDict[match.position2.teamId]) {
        team2 = [playersDictByMemberId[teamsDict[match.position2.teamId].members[0]].playerId, playersDictByMemberId[teamsDict[match.position2.teamId].members[1]].playerId];
    }
    const resultWinners = [];
    const resultLosers = [];

    resultWinners.push(parseInt(result.players.winner1?.id || result.players.winner1));
    resultLosers.push(parseInt(result.players.loser1?.id || result.players.loser1));

    if (result.players.winner2 === null) {
        return (team1[0] === resultWinners[0] || team1[0] === resultLosers[0]) && (team2[0] === resultWinners[0] || team2[0] === resultLosers[0]);
    }

    resultWinners.push(parseInt(result.players.winner2?.id || result.players.winner2));
    resultLosers.push(parseInt(result.players.loser2?.id || result.players.loser2));
    return (arraysEqual(team1, resultWinners) || arraysEqual(team1, resultLosers)) && (arraysEqual(team2, resultWinners) || arraysEqual(team2, resultLosers));
}

export const getEventRedirectUrl = (id, source) => {
    switch (source) {
        case 'scraped-import':
            return `/dualmatches/${id}`;
        case ImportedEventSources.TOURNAMENT:
        case ImportedEventSources.LEAGUE: {
            const importedEventType = ImportedEventTypeBySource[source];
            return canBuildImportedEventPath({ importedEventType, eventId: id })
                ? buildImportedEventPath({ importedEventType, eventId: id })
                : null;
        }
        case LeagueSessionSource:
            return `${config.leaguesAppHost}/leagues/session/${id}`;
        case 'tms-event':
        case 'tms-dual-match':
            return `/events/${id}`;
        default:
            return `/events/${id}`;
    }
};

export const getUseAnchorForEventLink = (source) => {
    switch (source) {
        case 'scraped-import':
        case LeagueSessionSource:
            return true;
        case ImportedEventSources.TOURNAMENT:
        case ImportedEventSources.LEAGUE:
        case 'tms-event':
        case 'tms-dual-match':
            return false;
        default:
            return false;
    }
};

export const isTeamMatchEvent = (source) => {
    switch (source) {
        case 'scraped-import':
        case 'tms-dual-match':
            return true;
        case 'tms-event':
            return false;
        default:
            return false;
    }
};

export const isTmsTeamMatchEvent = (source) => {
    return source === 'tms-dual-match';
};

export const isAdultTeamTennisMatch = value => value === 'team_match' || value === EventTypes.TEAM_MATCH;

export const isAdultTeamTennisMatchById = value => value === EventTypes.TEAM_MATCH;

export const getAddDrawGenderId = (event) => {
    const firstDivisionGenderId = event?.eventDivisions?.[0]?.gender?.id;
    if (Object.values(eventGenderTypeIds).includes(firstDivisionGenderId)) {
        return firstDivisionGenderId;
    }
    return eventGenderTypeIds.ALL;
};

export const isEventDirector = (event, memberId) => {
    if (event?.eventDirectors) {
        const director = _.find(event.eventDirectors, { memberId });
        return !!director;
    }

    return false;
};

export const shouldAddSplitAction = (isCurrentMemberAdmin, playerDivision, isPayAsTeam, isPaySeparately, divisionFormatTypeId) => {
    if (playerDivision) {
        const { registrationStatusTypeId, registrationGroupStatus, partners } = playerDivision;
        const isRegistered = registrationStatusTypeId === registrationStatusTypeIds.REGISTERED;
        const isPending = registrationGroupStatus === registrationGroupStatusTypeIds.PENDING;
        const needsPartner = registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER && partners?.length === 0;
        const isWithdrawn = registrationGroupStatus === registrationGroupStatusTypeIds.WITHDRAWN;
        const isRegisteredGroup = registrationGroupStatus === registrationGroupStatusTypeIds.REGISTERED;

        if (isWithdrawn || needsPartner) {
            return false;
        }

        if (isCurrentMemberAdmin && divisionFormatTypeId === divisionFormatTypeIds.DOUBLES) {
            const splitAllowedForPayAsTeam = isPayAsTeam && isPending;
            const splitAllowedForPaySeparately = isPaySeparately && (isPending || isRegisteredGroup);
            return isPending ||
                isRegistered ||
                splitAllowedForPayAsTeam ||
                splitAllowedForPaySeparately;
        }
    }

    return false;
};

export const isEventParticipant = (event, participantMemberId) => {
    if (event?.registeredMemberIds) {
        const registeredPlayer = _.find(event.registeredMemberIds, (memberId) => { return memberId == participantMemberId; });
        if (registeredPlayer) {
            return true;
        }
    }

    if (event?.withdrawnPlayers) {
        const registeredPlayer = _.find(event.withdrawnPlayers, (player) => { return player.memberId == participantMemberId; });
        if (registeredPlayer) {
            return true;
        }
    }

    return false;
};

export const shouldDisplayRoundNumbers = (eliminationType) => {
    const types = [
        drawEliminationTypes.ROUND_ROBIN,
        drawEliminationTypes.AD_HOC,
        drawEliminationTypes.DOUBLE_ELIMINATION,
    ];
    return types.includes(eliminationType);
};

export const shouldDisplayRoundMatchesAsList = (eliminationType) => {
    const types = [
        drawEliminationTypes.ROUND_ROBIN,
        drawEliminationTypes.AD_HOC,
    ];
    return types.includes(eliminationType);
};

export const shouldDisplayRoundNumbersV2 = (eliminationTypeId) => {
    const roundEliminationTypes = [
        drawEliminationTypeIds.ROUND_ROBIN,
        drawEliminationTypeIds.AD_HOC,
        drawEliminationTypeIds.DOUBLE_ELIMINATION,
    ];

    return roundEliminationTypes.includes(eliminationTypeId);
};

export const shouldDisplayRoundMatchesAsListV2 = (eliminationTypeId) => {
    const roundEliminationTypes = [
        drawEliminationTypeIds.ROUND_ROBIN,
        drawEliminationTypeIds.AD_HOC,
    ];

    return roundEliminationTypes.includes(eliminationTypeId);
};

const DEFAULT_COLUMN_NUMBER = 1;
export const getRoundSpacerClassName = (
    eliminationTypeId,
    rounds,
    selectedRound,
    columnNumber = DEFAULT_COLUMN_NUMBER,
) => {
    const isListTypeDraw = shouldDisplayRoundMatchesAsListV2(eliminationTypeId);
    if (isListTypeDraw || (columnNumber <= DEFAULT_COLUMN_NUMBER)) {
        return `round${DEFAULT_COLUMN_NUMBER}`;
    }

    const roundColumns = Array.from({ length: columnNumber - 1 }, (value, index) => index + DEFAULT_COLUMN_NUMBER);
    const spaceNumber = roundColumns.reduce((acc, column) => {
        const currentRound = rounds[selectedRound + column - 1];
        const nextRound = rounds[selectedRound + column];
        if (nextRound?.matches?.length < currentRound?.matches?.length) {
            return acc;
        }
        return acc - 1;
    }, columnNumber);

    return `round${spaceNumber}`;
};

const getFICRoundLabel = (round, nextRound) => {
    const matchCount = round.matches.length;
    let nextRoundMatchCount = 0;
    if (nextRound) {
        nextRoundMatchCount = nextRound.matches.length;
    }
    if (matchCount === matchCounts.FINAL) {
        return <span data-testid="round-label.c-finals"><span className="d-none d-sm-inline" data-testid="round-label.c-finals.full">C-Finals</span><span className="d-inline d-sm-none" data-testid="round-label.c-finals.short">C-F</span></span>;
    }
    if (matchCount === matchCounts.SEMI_FINAL) {
        return <span data-testid="round-label.c-semifinals"><span className="d-none d-sm-inline" data-testid="round-label.c-semifinals.full">C-Semifinals</span><span className="d-inline d-sm-none" data-testid="round-label.c-semifinals.short">C-SF</span></span>;
    }
    if (matchCount === matchCounts.QUARTER_FINAL && matchCount === nextRoundMatchCount) {
        return <span data-testid="round-label.c-quarterfinals-q"><span className="d-none d-sm-inline" data-testid="round-label.c-quarterfinals-q.full">C-Quarterfinals-Q</span><span className="d-inline d-sm-none" data-testid="round-label.c-quarterfinals-q.short">C-QF-Q</span></span>;
    }
    if (matchCount === matchCounts.QUARTER_FINAL && matchCount > nextRoundMatchCount) {
        return <span data-testid="round-label.c-quarterfinals"><span className="d-none d-sm-inline" data-testid="round-label.c-quarterfinals.full">C-Quarterfinals</span><span className="d-inline d-sm-none" data-testid="round-label.c-quarterfinals.short">C-QF</span></span>;
    }
    if (matchCount > matchCounts.QUARTER_FINAL && matchCount === nextRoundMatchCount) {
        return <span data-testid="round-label.c-round-of-q"><span className="d-none d-sm-inline" data-testid="round-label.c-round-of-q.full">C-Round of {matchCount * 2}-Q</span><span className="d-inline d-sm-none" data-testid="round-label.c-round-of-q.short">C-R{matchCount * 2}-Q</span></span>;
    }
    if (matchCount > matchCounts.QUARTER_FINAL && matchCount > nextRoundMatchCount) {
        return <span data-testid="round-label.c-round-of"><span className="d-none d-sm-inline" data-testid="round-label.c-round-of.full">C-Round of {matchCount * 2}</span><span className="d-inline d-sm-none" data-testid="round-label.c-round-of.short">C-R{matchCount * 2}</span></span>;
    }
};

const getDefaultRoundLabel = (round) => {
    const matchCount = round.matches.length;
    if (matchCount === 1) {
        return <span data-testid="round-label.finals"><span className="d-none d-sm-inline" data-testid="round-label.finals.full">Finals</span><span className="d-inline d-sm-none" data-testid="round-label.finals.short">F</span></span>;
    }
    if (matchCount === 2) {
        return <span data-testid="round-label.semifinals"><span className="d-none d-sm-inline" data-testid="round-label.semifinals.full">Semifinals</span><span className="d-inline d-sm-none" data-testid="round-label.semifinals.short">SF</span></span>;
    }
    if (matchCount === 4) {
        return <span data-testid="round-label.quarterfinals"><span className="d-none d-sm-inline" data-testid="round-label.quarterfinals.full">Quarterfinals</span><span className="d-inline d-sm-none" data-testid="round-label.quarterfinals.short">QF</span></span>;
    }
    return <span data-testid="round-label.round-of"><span className="d-none d-sm-inline" data-testid="round-label.round-of.full">Round of {matchCount * 2}</span><span className="d-inline d-sm-none" data-testid="round-label.round-of.short">R{matchCount * 2}</span></span>;
};

export const renderRoundLabel = (drawElimType, round, showSelectedRoundNameLabelForMobile, drawCategoryType = null, nextRound = null) => {
    if (shouldDisplayRoundNumbers(drawElimType) || shouldDisplayRoundNumbersV2(drawElimType)) {
        return (
            <span data-testid="round-label.round-number">
                <span className="d-none d-sm-inline" data-testid="round-label.round-number.full">Round {round.roundNumber || round.number}</span>
                <span className="d-inline d-sm-none" data-testid="round-label.round-number.short">
                    {showSelectedRoundNameLabelForMobile === true ? `Round${round.roundNumber || round.number}` : `R${round.roundNumber || round.number}`}
                </span>
            </span>
        );
    }

    if (drawElimType == 'match_play' || drawElimType === drawEliminationTypeIds.MATCH_PLAY) {
        return <span data-testid="round-label.matches">Matches</span>;
    }

    if (drawElimType === drawEliminationTypes.FEED_IN_CONSOLATION && drawCategoryType === drawCategoryTypeIds.CONSOLATION) {
        return getFICRoundLabel(round, nextRound);
    }

    return getDefaultRoundLabel(round);
};

export const drawStandingsEnabled = (drawElimType) => {
    const drawElimTypesWithStandings = ['round_robin', 'match_play', 'ad_hoc'];
    return drawElimTypesWithStandings.includes(drawElimType);
};
export const drawStandingsEnabledV2 = (drawElimTypeId) => {
    const drawElimTypesWithStandings = [drawEliminationTypeIds.ROUND_ROBIN, drawEliminationTypeIds.MATCH_PLAY, drawEliminationTypeIds.AD_HOC];
    return drawElimTypesWithStandings.includes(drawElimTypeId);
};

export const allStandingsEnabled = (draw) => {
    if (draw.eliminationTypeId === drawEliminationTypeIds.SINGLE_ELIMINATION || draw.eliminationTypeId === drawEliminationTypeIds.DOUBLE_ELIMINATION) {
        return true;
    }
    if (draw.eliminationTypeId === drawEliminationTypeIds.ROUND_ROBIN && draw.hasPlayoff) {
        return true;
    }
    if (draw.eliminationTypeId === drawEliminationTypeIds.AD_HOC && draw.hasPlayoff) {
        return true;
    }
    if (draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW) {
        return true;
    }
    return false;
};

export const isGroupPlayEvent = (event) => {
    return isEventType(event, EventTypes.FREE_PLAY);
};

export const isEventType = (event, eventTypeId) => {
    return event?.eventType?.id == eventTypeId || event?.eventTypeId == eventTypeId;
};

export const isPrivateEvent = (event) => {
    return event?.eventPermissionType?.value == 'private';
};

export const getDefaultEliminationType = (eventType, sportTypeId) => {
    const parsedEventType = parseInt(eventType);

    if (isPickleBallSportTypeEvent(sportTypeId)) {
        if (parsedEventType === EventTypes.FLEX_LEAGUE) {
            return drawEliminationTypeIds.AD_HOC;
        }

        return drawEliminationTypeIds.DOUBLE_ELIMINATION;
    }

    switch (parsedEventType) {
        case EventTypes.TOURNAMENT:
            return drawEliminationTypeIds.ROUND_ROBIN;
        case EventTypes.FLEX_LEAGUE:
            return drawEliminationTypeIds.AD_HOC;
        default:
            return drawEliminationTypeIds.MATCH_PLAY;
    }
};

export const getDefaultDivision = (values, currencyId, addName, sportTypeId) => {
    const { eventType } = values;
    const isVerifiedEvent = values.$verified && values.$verified === '2';
    const isPickleballEvent = sportTypeId === sportTypeIds.PICKLEBALL;

    const defaultDivision = {
        divisionFormatType: isPickleballEvent ? divisionFormatTypeIds.DOUBLES : divisionFormatTypeIds.SINGLES,
        gender: 1,
        utrRange: isPickleballEvent ? [1, 10] : [1, 16],
        ageRange: [0, Object.keys(eventDivisionAgeSliderDictionary).length - 1],
        entryFee: null,
        allowWaitlist: true,
        restrictAge: false,
        restrictAgeByDateType: null,
        restrictByRating: false,
        acceptance: {
            type: acceptanceValues.WAIT_LIST,
        },
    };
    if (!isPickleballEvent) {
        defaultDivision.ballTypeValue = 'Yellow';
    }
    if (values.eventDivisions && values.eventDivisions.length > 0) {
        defaultDivision.eliminationType = values.eventDivisions[0].eliminationType;
    } else {
        defaultDivision.eliminationType = getDefaultEliminationType(eventType, sportTypeId);
    }
    defaultDivision.maxPlayers = defaultDivision?.acceptance?.type === acceptanceValues.WAIT_LIST ? null : 16;
    defaultDivision.currencyId = currencyId;
    if (isVerifiedEvent) {
        defaultDivision.officialsType = '2';
    }
    if (addName) {
        if (isPickleballEvent) {
            defaultDivision.name = 'Level-based doubles';
        } else {
            defaultDivision.name = 'Level-based singles';
        }
    }
    return defaultDivision;
};

export const getAllowedDrawEliminationTypes = (
    drawEliminationTypes = [],
    isFeedInConsolationFlagEnabled = false,
    isProDrawCreationEnabled = false,
    isForEventDrawCreateEdit = false,
    enableFeedInBackDrawType = false,
) => {
    const eliminationTypes = drawEliminationTypes.filter((elimType) => {
        if (elimType?.id === drawEliminationTypeIds.DOUBLE_ELIMINATION) {
            return true;
        }
        if (elimType?.id === drawEliminationTypeIds.FEED_IN_CONSOLATION) {
            return isFeedInConsolationFlagEnabled;
        }
        if (elimType?.id === drawEliminationTypeIds.FEED_IN_BACK_DRAW) {
            return isForEventDrawCreateEdit && enableFeedInBackDrawType;
        }
        return true;
    });

    if (isForEventDrawCreateEdit && isProDrawCreationEnabled) {
        eliminationTypes.push({
            id: drawEliminationTypeIds.PRO_DRAWS,
            value: 'pro_draw',
            label: 'Pro Draw',
            description: 'Pro Draw',
            order: drawEliminationTypeIds.PRO_DRAWS,
        });
    }
    return eliminationTypes;
};

export const getProDrawEliminationTypes = (
    drawEliminationTypes = [],
    isFeedInConsolationFlagEnabled = false,
    isProDrawCreationEnabled = false,
    isForEventDrawCreateEdit = false,
) => {
    const eliminationTypeIdsToEnable = [
        drawEliminationTypeIds.SINGLE_ELIMINATION,
        drawEliminationTypeIds.FIRST_MATCH_BACKDRAW,
        drawEliminationTypeIds.FEED_IN_BACK_DRAW,
    ];

    const allowedElimTypes = getAllowedDrawEliminationTypes(
        drawEliminationTypes,
        isFeedInConsolationFlagEnabled,
        isProDrawCreationEnabled,
        isForEventDrawCreateEdit,
        true,
    );

    return allowedElimTypes.filter((elimType) => {
        return eliminationTypeIdsToEnable.includes(elimType.id);
    });
};

export const getQualifyingDrawEliminationTypes = (
    drawEliminationTypes = [],
    isFeedInConsolationFlagEnabled = false,
    isProDrawCreationEnabled = false,
    isForEventDrawCreateEdit = false,
) => {
    const eliminationTypeIdsToEnable = [
        drawEliminationTypeIds.SINGLE_ELIMINATION,
        drawEliminationTypeIds.FIRST_MATCH_BACKDRAW,
    ];

    const allowedElimTypes = getAllowedDrawEliminationTypes(
        drawEliminationTypes,
        isFeedInConsolationFlagEnabled,
        isProDrawCreationEnabled,
        isForEventDrawCreateEdit,
        false,
    );

    return allowedElimTypes.filter((elimType) => {
        return eliminationTypeIdsToEnable.includes(elimType.id);
    });
};

export const getAllowedEventTypes = ({ club, hsEventType, sportTypeId = sportTypeIds.TENNIS }) => {
    const { tierType: clubTierType } = club;
    let { permissionedTmsEventTypes } = clubTierType;

    if (sportTypeId) {
        permissionedTmsEventTypes = permissionedTmsEventTypes.filter(eventType => eventType.sportTypeId === sportTypeId);
    }

    // UTR Team Tennis is obsolete (Non-tennis and Virtual are excluded by the API lookups).
    permissionedTmsEventTypes = permissionedTmsEventTypes.filter(eventType => eventType.id !== EventTypes.TEAM_LEAGUE);

    if (clubTierType.isHighSchool) {
        if (hsEventType === EventTypes.TOURNAMENT) {
            permissionedTmsEventTypes = permissionedTmsEventTypes.filter(eventType => (eventType.id === EventTypes.TOURNAMENT || eventType.id === EventTypes.DUAL_MATCH));
        } else if (hsEventType === EventTypes.LEAGUE_OR_TEAM) {
            permissionedTmsEventTypes = permissionedTmsEventTypes.filter(eventType => eventType.id !== EventTypes.DUAL_MATCH);
        } else {
            // do nothing
        }
    }

    return [...permissionedTmsEventTypes];
};

export const eventDivisionIsPlatformPaymentOrFree = (division) => {
    if (division.paymentType) {
        return !division.paymentType.isOffPlatform;
    }

    // null paymentType indicates free event division.
    return true;
};

export const eventDivisionsContainPlatformPaymentOrFree = (eventDivisions) => {
    return !!(eventDivisions.find((div) => {
        return eventDivisionIsPlatformPaymentOrFree(div);
    }));
};

export const getAllowedScoringFormats = (
    scoringTypes,
    eventTypeId,
    divisionFormatTypeId,
    ballType,
) => {
    let filteredScoringTypes = [...scoringTypes];
    let ballTypeId = ballType;
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(parseInt(ballType))) {
        ballTypeId = _.find(ballTypes, { value: ballTypeId })?.id;
    }
    // don't show `2_sets_w_tiebreak` for dingles
    const isDingles = parseInt(divisionFormatTypeId) === divisionFormatTypeIds.DINGLES;
    if (isDingles) {
        filteredScoringTypes = _.filter(filteredScoringTypes, (scoringType) => {
            return scoringType.value !== '2_sets_w_tiebreak';
        });
    }
    // don't show `single set` for verified singles yellow ball
    filteredScoringTypes = _.filter(filteredScoringTypes, (scoringType) => {
        const isFlexLeagueEvent = eventTypeId === EventTypes.FLEX_LEAGUE && scoringType.value === 'single_set' && parseInt(ballTypeId) === ballTypeIds.YELLOW && parseInt(divisionFormatTypeId) === divisionFormatTypeIds.SINGLES;
        return !isFlexLeagueEvent;
    });

    return filteredScoringTypes;
};

export const getTimeZones = (coreUITimeZones) => {
    const timeZones = _.cloneDeep(coreUITimeZones);
    timeZones.sort((tz1, tz2) => {
        const offset1 = moment().utcOffset(tz1.offset)._offset;
        const offset2 = moment().utcOffset(tz2.offset)._offset;
        if (offset1 !== offset2) {
            if (offset1 < offset2) { return -1; }
            if (offset1 > offset2) { return 1; }
        } else {
            const label1 = tz1.label?.toUpperCase();
            const label2 = tz2.label?.toUpperCase();
            if (label1 < label2) {
                return -1;
            }
            if (label1 > label2) {
                return 1;
            }
        }
        return 0;
    });
    timeZones.forEach((timeZone) => {
        timeZone.label = `${timeZone.label} (GMT${timeZone.offset})`;
    });

    return timeZones;
};

export const PopUpTypes = {
    ADD_PAYMENT: {
        type: 'add_payment',
        message: 'We recommend you finish filling out this event creation form, save it as a draft and then set up your payment account through the link below so you do not lose progress on this form. You can then return to this draft event from your club profile page to set up entry fees and publish the event for registration.',
    },
    UNPUBLISH_EVENT: {
        type: 'unpublish_event',
        message: 'Unpublishing this event will mean that no players will be able to search, view or register for this event. You may re-publish the event in the future.',
    },
    REMOVE_EVENT_DIRECTOR: {
        type: 'remove_event_director',
        message: 'Do you want to remove Event Provider?',
    },
    REMOVE_EVENT_OFFICIAL: {
        type: 'remove_event_official',
        message: 'Deleting this official will remove them from any match in this event that they have been assigned to. Are you sure?',
    },
    REMOVE_MATCH_OFFICIAL: {
        type: 'remove_match_official',
        message: 'Do you want to remove Match Official?',
    },
    CHANGE_SCHEDULE_TYPE: {
        type: 'change_schedule_type',
        message: 'Changing the schedule type will erase all previous schedule data for this event. Are you sure?',
    },
};

export const mapDivisionsForSave = (divisions, acceptanceListRankingTypes, acceptanceListRoundTypes, useAcceptanceList) => {
    divisions.map(div => {
        div.minUtr = div.utrRange[0];
        div.maxUtr = div.utrRange[1];
        div.minAge = eventDivisionAgeSliderDictionary[div.ageRange[0]];
        div.maxAge = eventDivisionAgeSliderDictionary[div.ageRange[1]];

        if (useAcceptanceList) {
            if (div.acceptance?.type === acceptanceValues.LIST) {
                div.allowWaitlist = true;
                div.acceptance.buckets = div.acceptance.list.buckets;
                if (div.acceptance.list.type === acceptanceRankingValues.UTR) {
                    div.acceptance.acceptanceRatingType = div.acceptance.list.rating;
                    div.acceptance.acceptanceRankingType = null;
                } else {
                    div.acceptance.acceptanceRatingType = null;
                    div.acceptance.acceptanceRankingType = _.find(acceptanceListRankingTypes, { value: div.acceptance.list.type }).id;
                }
            } else {
                div.allowWaitlist = div.acceptance?.type === acceptanceValues.WAIT_LIST;
                div.acceptance = null;
            }
        } else {
            div.acceptance = null;
        }

        if (div.ballTypeValue == _.find(ballTypes, { id: ballTypeIds.YELLOW }).value) {
            div.ballType = null;
        } else {
            div.ballType = _.find(ballTypes, { value: div.ballTypeValue })?.id;
        }

        return div;
    });

    return divisions;
};

export const populateRegData = (event) => {
    if (event.eventDivisions && event.currentMember.registrationDetails) {
        event.eventDivisions.map((div) => {
            const registered = event.currentMember.registrationDetails.find(reg => reg.divisionId === div.id);
            if (registered) {
                event.registered = true;
                div.registered = true;
                if (registered.partners?.length) {
                    [div.player2] = registered.partners;
                }
            }
            return div;
        });
    }
    return event;
};

export const getDefaultSortParamForDivision = (division) => {
    if (division?.allowWaitlist) {
        return sortParamTypes.REGDATE;
    }
    if (division?.divisionFormatType?.id === 2 || division?.divisionFormatType?.id === 3 || division?.divisionFormatTypeId === 2 || division?.divisionFormatTypeId === 3) {
        return sortParamTypes.DOUBLESUTR;
    }
    return sortParamTypes.SINGLESUTR;
};

export const getDefaultSortParamForPickleballDivision = (division) => {
    if (division?.allowWaitlist) {
        return sortParamTypes.REGDATE;
    }
    if (division?.divisionFormatTypeId === 2 || division?.divisionFormatTypeId === 3) {
        return sortParamTypes.DOUBLESPKB;
    }
    return sortParamTypes.SINGLESPKB;
};

export const getDefaultRatingSortParamForDivision = (division, sportTypeId) => {
    if (division?.divisionFormatType?.id === 2 || division?.divisionFormatType?.id === 3 || division?.divisionFormatTypeId === 2 || division?.divisionFormatTypeId === 3) {
        return sportTypeId === sportTypeIds.TENNIS ? sortParamTypes.DOUBLESUTR : sortParamTypes.DOUBLESPKB;
    }
    return sportTypeId === sportTypeIds.TENNIS ? sortParamTypes.SINGLESUTR : sortParamTypes.SINGLESPKB;
};

export const getDefaultSortOrderForDivision = (division) => {
    if (division?.allowWaitlist) {
        return SortType.ASC;
    }
    return SortType.DESC;
};

export function isOrganizer(event) {
    const eventDirectorRoles = ['admin', 'owner'];
    const eventDirectorRoleIds = [directorRoleIds.ADMIN, directorRoleIds.OWNER];
    const clubDirectorRoles = ['Admin', 'Owner'];
    const clubDirectorRoleIds = [clubMemberRoleIds.ADMIN, clubMemberRoleIds.OWNER];

    return eventDirectorRoleIds.includes(event?.currentMember?.eventDirectorRoleId) ||
        eventDirectorRoles.includes(event?.currentMember?.eventDirectorRole?.value) ||
        clubDirectorRoleIds.includes(event?.currentMember?.clubMemberRoleId) ||
        clubDirectorRoles.includes(event?.currentMember?.clubMemberRole?.value);
}

export function isVolunteer(event) {
    return (
        event?.currentMember?.eventDirectorRoleId === directorRoleIds.VOLUNTEER
    );
}

export const isDivisionRefundEligible = (division, event) => {
    return (division.autoRefundEnabled && new Date(`${division.maxAutoRefundDateUtc}Z`) > new Date()) ||
        (event.verificationFee > 0 && (division.entryFee === 0 || division.entryFee === null));
};

export const mapTemplateDivisionsForEdit = (divisions, acceptanceListRoundTypes, sportTypeId, eventVerifiedTypeId) => {
    const qualifyingRound = _.find(acceptanceListRoundTypes, { value: 'QUALIFYING' });

    divisions.forEach((division) => {
        if (division.ballType === null || division.ballType === undefined || division.ballType === '') {
            division.ballTypeValue = _.find(ballTypes, { id: ballTypeIds.YELLOW })?.value;
        } else {
            division.ballTypeValue = _.find(ballTypes, { id: division.ballType })?.value;
        }

        if (!division.utrRange) {
            division.utrRange = [division.minUtr ?? 1, division.maxUtr ?? (sportTypeId === sportTypeIds.TENNIS ? 16 : 10)];
        }

        if (!division.ageRange) {
            division.ageRange = [
                parseInt(getEventDivisionAgeKeyByValue(division.minAge ?? 0)),
                parseInt(getEventDivisionAgeKeyByValue(division.maxAge ?? Object.keys(eventDivisionAgeSliderDictionary).length - 1)),
            ];
        }

        if (division?.acceptance?.buckets?.length > 0) {
            division.acceptance = {
                ...division.acceptance,
                type: acceptanceValues.LIST,
                list: {
                    type: division.acceptance.acceptanceRatingType ? acceptanceRankingValues.UTR : acceptanceRankingValues.PTT,
                    rating: division.acceptance.acceptanceRatingType,
                    buckets: division.acceptance.buckets,
                },
            };
            if (_.findIndex(division.acceptance.list.buckets, { type: qualifyingRound.id }) === -1) {
                division.acceptance.list.buckets.push({
                    type: qualifyingRound.id,
                    order: 2,
                    count: null,
                });
            }
        } else {
            division.acceptance = {
                type: division.allowWaitlist ? acceptanceValues.WAIT_LIST : acceptanceValues.CAPPED_REGISTRATION,
            };
        }

        if (parseInt(division.maxPlayers) === noMaximumLabelValue && division?.acceptance?.type === acceptanceValues.WAIT_LIST) {
            division.maxPlayers = null;
        }

        // change max players to min 4 if chosen less than 4 before making it verified
        if (parseInt(eventVerifiedTypeId) === EventVerifiedTypes.Sanctioned && parseInt(division.maxPlayers) < verifiedAcceptanceMinValue) {
            division.maxPlayers = verifiedAcceptanceMinValue;
        }
    });

    return divisions;
};

export const mapEventTemplateValuesForSave = (values, acceptanceListRankingTypes, acceptanceListRoundTypes) => {
    if (values.template) {
        values.template.eventDivisions = mapDivisionsForSave(values.eventDivisions, acceptanceListRankingTypes, acceptanceListRoundTypes, true);
        values.template.eventQuestions = values.eventQuestions;
        values.template.eventClubJoinRequestType = values.$clubJoin ? 2 : 1;
        values.template.playerVisibility = values.playerVisibility;
        values.template.playerCountVisibility = values.playerCountVisibility;
        values.template.needPartnerVisibility = values.needPartnerVisibility;
        values.template.allowPlayerPosting = values.allowPlayerPosting;
        values.template.eventPermissionType = values.eventPermissionType;

        if (values.template.tour) {
            values.template.tours = [values.template.tour];
        } else {
            values.template.tours = [];
        }
        if (values.template.eventType === EventTypes.VIRTUAL) {
            values.template.verified = 1; // not verified
        }

        values.template.utrType = values.template.verified;
    }

    if (values.percentFeeDefault) {
        values.percentFee = null;
    }

    return values;
};

export const getClubListForCreateEvent = (club) => {
    let clubsList;
    const PERSONAL_CLUB_TIERTYPE_ID = 1;
    if (club?.rows?.length > 0) {
        clubsList = [];
        club.rows.forEach((row) => {
            if (row.tierTypeId !== PERSONAL_CLUB_TIERTYPE_ID && isClubActive(row) && clubCanRunEvents(row) && (row.clubMemberRole.value === 'Owner' || row.clubMemberRole.value === 'Admin')) {
                clubsList.push(row);
            }
        });
    }

    return clubsList;
};

export const sortEventOfficialsByRoleThenAlphabetically = officialsArray => {
    let sortedOfficials = [];
    if (officialsArray) {
        const headOfficials = officialsArray.filter(official => official.role === 'head official/referee');
        headOfficials.sort((a, b) => {
            if (a.lastName?.toLowerCase() < b.lastName?.toLowerCase()) {
                return -1;
            }
            if (a.lastName?.toLowerCase() > b.lastName?.toLowerCase()) {
                return 1;
            }
            return 0;
        });
        const chairUmpires = officialsArray.filter(official => official.role === 'chair umpire');
        chairUmpires.sort((a, b) => {
            if (a.lastName?.toLowerCase() < b.lastName?.toLowerCase()) {
                return -1;
            }
            if (a.lastName?.toLowerCase() > b.lastName?.toLowerCase()) {
                return 1;
            }
            return 0;
        });
        sortedOfficials = headOfficials.concat(chairUmpires);
    }
    return sortedOfficials;
};

export const isCompassDraw = drawCategoryType => {
    const compassDrawCategoryIds = [
        drawCategoryTypeIds.COMPASS_EAST,
        drawCategoryTypeIds.COMPASS_NORTH,
        drawCategoryTypeIds.COMPASS_SOUTH,
        drawCategoryTypeIds.COMPASS_WEST,
        drawCategoryTypeIds.COMPASS_NORTHEAST,
        drawCategoryTypeIds.COMPASS_NORTHWEST,
        drawCategoryTypeIds.COMPASS_SOUTHEAST,
        drawCategoryTypeIds.COMPASS_SOUTHWEST,
    ];

    return compassDrawCategoryIds.includes(drawCategoryType);
};

export const shouldDisplayDraws = draw => {
    if (isCompassDraw(draw.categoryTypeId)) {
        return draw.hasMatches;
    }
    return true;
};

export const isDrawMainOrQualifying = drawCategoryType => {
    const categoryTypeIds = [drawCategoryTypeIds.MAIN, drawCategoryTypeIds.QUALIFYING];
    const categoryTypes = ['main', 'qualifying'];

    return categoryTypes.includes(drawCategoryType?.value) ||
        categoryTypeIds.includes(drawCategoryType);
};

export const isProDrawOrProDrawQualifier = (drawCategoryType) => {
    return drawCategoryType?.value === drawEliminationTypes.PRO_DRAW ||
        drawCategoryType?.value === drawEliminationTypes.PRO_DRAW_QUALIFIER ||
        drawCategoryType === drawCategoryTypeIds.PRO_DRAW ||
        drawCategoryType === drawCategoryTypeIds.PRO_DRAW_QUALIFIER;
};

export const isDrawAutofillEnabledForCategory = (drawCategoryType) => {
    return isProDrawOrProDrawQualifier(drawCategoryType) ||
        isDrawAutoPlayoff(drawCategoryType) ||
        isDrawMainOrQualifyingOrCompassEast(drawCategoryType);
};

export const isDrawAutofillEnabledForEliminationType = (eliminationTypeId) => {
    return eliminationTypeId !== drawEliminationTypeIds.MATCH_PLAY;
};

export const shouldAllowPlayerListActionsEventDesk = (drawCategoryType) => {
    return isProDrawOrProDrawQualifier(drawCategoryType) ||
        isDrawMainOrQualifyingOrCompassEast(drawCategoryType);
};

export const isDrawAutoPlayoff = drawCategoryType => {
    return drawCategoryType?.value === 'auto_playoff' ||
        drawCategoryType === drawCategoryTypeIds.AUTO_PLAYOFF;
};

export const isCompassEastDraw = drawCategoryType => {
    return drawCategoryType?.value === 'compass_east' ||
        drawCategoryType === drawCategoryTypeIds.COMPASS_EAST;
};

export const isDrawMainOrQualifyingOrCompassEast = drawCategoryType => {
    return isDrawMainOrQualifying(drawCategoryType) ||
        isCompassDraw(drawCategoryType);
};

export const addOOPTimeLabel = (matchArr, timeZone) => {
    const matches = matchArr ?? [];
    let lastMatchWithTime = null;
    matches.forEach((match, i) => {
        if (match.includesTime && match.date && !match.notBefore) {
            lastMatchWithTime = i;
            match.timeLabel = `Starts at ${moment.utc(match.date).tz(timeZone.value).format('h:mma')}`;
        } else if (match.date && match.notBefore) {
            if (lastMatchWithTime !== null) {
                match.timeLabel = `Followed by, not before ${moment.utc(match.date).tz(timeZone.value).format('h:mma')}`;
            } else {
                match.timeLabel = `Not before ${moment.utc(match.date).tz(timeZone.value).format('h:mma')}`;
                lastMatchWithTime = i;
            }
        } else if (lastMatchWithTime !== null) {
            match.timeLabel = `Followed by`;
        }
    });
    return matches;
};

export const doesPlayersHaveUtrDiff = (players, maxUtrDiff) => {
    let isDiffGreaterThanMax = false;

    // filters players having, 100% relaible rating
    const filteredPlayers = players.filter(player => player?.ratingStatusSingles?.toLowerCase() === 'rated' && player.ratingProgressSingles === 100);

    filteredPlayers.sort((a, b) => {
        if (a.singlesUtr < b.singlesUtr) {
            return -1;
        }

        if (a.singlesUtr > b.singlesUtr) {
            return 1;
        }

        return 0;
    });

    if (filteredPlayers.length > 0 && filteredPlayers[filteredPlayers.length - 1].singlesUtr - filteredPlayers[0].singlesUtr >= maxUtrDiff) {
        isDiffGreaterThanMax = true;
    }

    return isDiffGreaterThanMax;
};

export const getMatchDraw = (match, draws) => {
    let matchDraw;

    draws.forEach(draw => {
        if (draw.rounds?.some(round => round.matches.some(roundMatch => roundMatch.id === match.id))) {
            matchDraw = draw;
        } else if (draw.childDraws?.length) {
            draw.childDraws.forEach(childDraw => {
                if (childDraw.rounds?.some(round => round.matches.some(roundMatch => roundMatch.id === match.id))) {
                    matchDraw = childDraw;
                    matchDraw.parentDrawName = draw.name;
                }
            });
        }
    });

    return matchDraw;
};

export const matchPositionNumbers3r = [
    [['1', '2'], ['3', '4']], // r1
    [['1', '4'], ['2', '3']], // r2
    [['1', '3'], ['2', '4']], // r3
];
export const matchPositionNumbers5r = [
    [['1', '2'], ['3', ''], ['4', '5']], // r1
    [['1', '4'], ['5', ''], ['2', '3']], // r2
    [['1', '3'], ['2', '5'], ['4', '']], // r3
    [['1', '5'], ['3', '4'], ['2', '']], // r4
    [['1', ''], ['2', '4'], ['3', '5']], // r5
];

export const validatePTTRoundRobinPositionV2 = (draw) => {
    const PTTcheck = {};
    const matchPositionNumbers = draw.rounds.length === 3 ? matchPositionNumbers3r : matchPositionNumbers5r;
    // loop through rounds and matches and store match position numbers in an array under a playerId key
    draw.rounds.forEach((r, ri) => {
        r.matches.forEach((m, mi) => {
            if (m.position1?.memberId && draw.members.some(member => member.memberId === m.position1.memberId)) {
                PTTcheck[m.position1.memberId] = PTTcheck[m.position1.memberId] || [];
                PTTcheck[m.position1.memberId].push(matchPositionNumbers[ri][mi][0]);
            }
            if (m.position2?.id && draw.members.some(member => member.memberId === m.position2.memberId)) {
                PTTcheck[m.position2.memberId] = PTTcheck[m.position2.memberId] || [];
                PTTcheck[m.position2.memberId].push(matchPositionNumbers[ri][mi][1]);
            }
        });
    });
    // check if there are multiple unique numbers in an array
    let error;
    Object.entries(PTTcheck).forEach(([key, val]) => {
        if (_.uniq(val).length > 1) {
            // return the one occuring less to show validation error
            const positionNumber = val.sort((a, b) => val.filter(v => v === b).length - val.filter(v => v === a).length).pop();
            error = { memberId: key, positionNumber };
        }
    });

    return error;
};

export const isEventSanctioned = (utrType) => {
    return utrType?.id === EventVerifiedTypes.Sanctioned || utrType?.value === 'sanctioned' || utrType === utrTypeIds.SANCTIONED;
};

export const isOfficialHsEvent = (hsEventType) => {
    return hsEventType?.id === EventHsTypes.OFFICIAL || hsEventType?.value === 'official' || hsEventType === EventHsTypes.OFFICIAL;
};

export const isEventOfficialHsEvent = (event) => {
    return isHighSchoolClub(event?.club?.tierType || event?.club?.tierTypeId) &&
        isEventSanctioned(event?.utrType || event?.utrTypeId) &&
        isOfficialHsEvent(event?.hsEventType?.id || event?.hsEventTypeId);
};

export const eventEnum = {
    dualMatchEvent: 8,
    adultTeamTennisEvent: 13,
};

export const getEventCurrencySymbol = (allowOffPlatformPayments, currency) => {
    if (allowOffPlatformPayments) {
        return null;
    }

    return currency?.abbreviation || '$';
};

export const getPaidHitterExperience = (paidHits, playerExperienceTypes, coachingExperienceTypes, userIsPowered, showDecimals) => {
    let hitterPlayingExperience;
    let hitterCoachingExperience;

    if (paidHits) {
        const playerRating = getRatingDetails(setRatingChoice({ player: paidHits.player }), userIsPowered, showDecimals);

        if (paidHits.experienceTypeId !== null && paidHits?.experienceStatus !== null) {
            const playerExperienceLabel = playerExperienceTypes?.find(type => type.id === paidHits.experienceTypeId)?.label;
            hitterPlayingExperience = `${paidHits.experienceStatus === 'former' ? 'Former' : 'Current'} ${playerExperienceLabel}`;
        }
        if (paidHits.coachingExperienceTypeId !== null && paidHits?.coachingExperienceStatus !== null) {
            const coachingExperienceLabel = coachingExperienceTypes?.find(type => type.id === paidHits.coachingExperienceTypeId)?.label;
            hitterCoachingExperience = `${paidHits.coachingExperienceStatus === 'former' ? 'Former' : 'Current'} ${coachingExperienceLabel}`;
        }
        const descriptionItems = [
            ...(playerRating ? [`${playerRating.value}`] : []),
            ...(hitterPlayingExperience ? [`${hitterPlayingExperience}`] : []),
            ...(hitterCoachingExperience ? [`${hitterCoachingExperience}`] : []),
        ];
        return descriptionItems.join(' \u2022 ');
    }
    return null;
};

export const getOrganizerRating = (memberId, organizerRatings) => {
    return (organizerRatings || []).find(o => o.memberId === memberId);
};

export const getPBRProgressScaleReadings = (rating) => {
    return [
        {
            name: 'R1',
            value: 16,
            color: '#E0004F',
            arrow: rating === 'R1',
        },
        {
            name: 'O1',
            value: 16,
            color: '#FF8825',
            arrow: rating === 'O1',
        },
        {
            name: 'O2',
            value: 16,
            color: '#FF8825',
            arrow: rating === 'O2',
        },
        {
            name: 'G1',
            value: 16,
            color: '#0EDA3F',
            arrow: rating === 'G1',
        },
        {
            name: 'G2',
            value: 16,
            color: '#0EDA3F',
            arrow: rating === 'G2',
        },
        {
            name: 'G3',
            value: 16,
            color: '#0EDA3F',
            arrow: rating === 'G3',
        },
    ];
};
export const isEventPTT = (event) => {
    return event?.eventTours?.some(tour => tour.id === EventTours.PTT);
};

const getPlayableDivisions = (event) => {
    return event?.eventDivisions?.filter(div => !div.isEventEntryFeeDivision) || [];
};

export const areAllPlayableDivisionsAnnualPowerOnly = (event) => {
    const playableDivisions = getPlayableDivisions(event);
    return playableDivisions.length > 0 && playableDivisions.every(div => div.annualPoweredOnly === true);
};

export const isAnyPlayableDivisionAnnualPowerOnly = (event) => {
    return getPlayableDivisions(event).some(div => div.annualPoweredOnly === true);
};

export const isHybridAnnualPowerEvent = (event) => {
    return isAnyPlayableDivisionAnnualPowerOnly(event) && !areAllPlayableDivisionsAnnualPowerOnly(event);
};

export const isAnySelectedDivisionAnnualPowerOnly = (event, selectedDivisionIds) => {
    return getPlayableDivisions(event).some(div => selectedDivisionIds?.includes(div.id) && div.annualPoweredOnly === true);
};

export const areAllSelectedDivisionsAnnualPowerOnly = (event, selectedDivisionIds) => {
    const selectedDivisions = getPlayableDivisions(event).filter(div => selectedDivisionIds?.includes(div.id));
    return selectedDivisions.length > 0 && selectedDivisions.every(div => div.annualPoweredOnly === true);
};

export const shouldCheckForEventDuration = (values = {}, schedule = {}) => {
    return values?.eventType !== EventTypes.FLEX_LEAGUE &&
        values?.$verified === EventVerificationTypes.VERIFIED &&
        !schedule?.maxEventEndUtc;
};

export const getEventDuration = (schedule) => {
    const start = moment.utc(schedule.eventStartUtc);
    const end = moment.utc(schedule.eventEndUtc);

    if (!start || !end) { return 0; }

    return end.diff(start, 'days');
};

export const getMaxEventDuration = (clubSettings) => {
    return clubSettings?.maxVerifiedEventDuration || DEFAULT_MAX_VERIFIED_EVENT_DURATION;
};

export const getMaxEventEndDate = (
    values = {},
    schedule = {},
    timezone = {},
    maxVerifiedEventDuration = DEFAULT_MAX_VERIFIED_EVENT_DURATION,
) => {
    return values.$verified === EventVerificationTypes.VERIFIED
        ? moment.utc(schedule.eventStartUtc).tz(timezone.value).add(maxVerifiedEventDuration - 1, 'days')
        : null;
};

const ImportedEventGenderLabels = {
    male: 'Male',
    female: 'Female',
    coed: 'Co-ed',
};

const ImportedEventGameTypeLabels = {
    singles: 'Singles',
    doubles: 'Doubles',
};

const getImportedEventDatesString = (startDate, endDate) => {
    if (!startDate) {
        return null;
    }
    const start = moment.utc(startDate);
    const end = endDate ? moment.utc(endDate) : start;

    return getEventDateString({
        eventStartDate: start.format('D'),
        eventEndDate: end.format('D'),
        eventStartMonth: start.format('MMM'),
        eventEndMonth: end.format('MMM'),
        eventStartYear: start.format('YYYY'),
        eventEndYear: end.format('YYYY'),
    }, true);
};

const normalizeImportedProfileEvent = (card) => {
    return {
        id: card.id,
        name: card.name,
        eventSchedule: { eventDatesString: getImportedEventDatesString(card.startDate, card.endDate) },
        source: ImportedEventSourceByType[card.importedEventType],
        sportTypeId: card.sportTypeId ?? sportTypeIds.TENNIS,
        registeredCount: card.playerCount,
        importedFormats: (card.gameTypes || []).map(gameType => ImportedEventGameTypeLabels[gameType]).filter(Boolean),
        importedGender: ImportedEventGenderLabels[card.gender] || null,
        importedEventType: card.importedEventType,
        importedPageReady: card.importedPageReady,
        importedEventVerified: card.importedEventVerified,
        importSource: card.importSource,
        thirdPartyIdentifier: card.thirdPartyIdentifier,
        startDate: card.startDate,
    };
};

const LeagueSessionGenderLabels = {
    1: 'Co-ed',
    2: 'Male',
    3: 'Female',
};

const LeagueSessionTypeLabels = {
    1: 'Team Tournament',
    2: 'Multi-Round',
};

const MIN_UTRP_RATING = 1;
const CITY_OFFSET_FROM_END = 3;
const STATE_OFFSET_FROM_END = 2;

const getCityStateFromFormattedAddress = (formattedAddress) => {
    const parts = (formattedAddress || '').split(',').map(part => part.trim()).filter(Boolean);
    if (parts.length < CITY_OFFSET_FROM_END) {
        return null;
    }
    const city = parts[parts.length - CITY_OFFSET_FROM_END];
    const state = parts[parts.length - STATE_OFFSET_FROM_END].split(' ')[0];

    return (city && state) ? `${city}, ${state}` : null;
};

const getLeagueSessionRatingRange = (session) => {
    const min = session.conferenceRatings?.utrRangeMin ?? session.conferenceMinUtr;
    const max = session.conferenceRatings?.utrRangeMax ?? session.conferenceMaxUtr;
    if (min === null || min === undefined || max === null || max === undefined) {
        return null;
    }
    const ratingType = session.sportTypeId === sportTypeIds.PICKLEBALL ? ratingTypes.pickleball : ratingTypes.tennis;
    const minRating = min === 0 ? MIN_UTRP_RATING : min;

    return `${ratingType} ${minRating.toFixed(1)} - ${max.toFixed(1)}`;
};

const normalizeLeagueSessionProfileEvent = (session) => {
    const location = session.location || {};

    return {
        id: session.sessionId,
        sessionId: session.sessionId,
        name: session.sessionName,
        source: LeagueSessionSource,
        sportTypeId: session.sportTypeId,
        isLeaguesMatch: true,
        startDate: session.sessionStartDate,
        eventSchedule: { eventDatesString: getImportedEventDatesString(session.sessionStartDate, session.sessionEndDate) },
        leagueSessionType: LeagueSessionTypeLabels[session.sessionTypeId] || null,
        leagueSessionOrganizer: session.conferenceName || session.leagueName || null,
        leagueSessionLocation: getCityStateFromFormattedAddress(location.formattedAddress) || location.placeName || null,
        leagueSessionRatingRange: getLeagueSessionRatingRange(session),
        leagueSessionAgeRange: ageDisplay([session.conferenceMinAge, session.conferenceMaxAge]),
        leagueSessionGender: LeagueSessionGenderLabels[session.conferenceGenderTypeId] || null,
    };
};

// clubId is what makes the organizer part a link to the club page. A league session's organizer is a
// conference or league name with no club behind it, so it stays plain text.
const buildSubtitleParts = (organizer, location, title, organizerClubId = null) => {
    const parts = [];
    if (organizer && organizer !== title) {
        parts.push({ text: organizer, clubId: organizerClubId || null });
    }
    if (location) {
        parts.push({ text: location, clubId: null });
    }
    return parts;
};

const UNIFIED_ROLE_PLAYER = 'Player';
const UNIFIED_ROLE_PROVIDER = 'Provider';

const buildImportedCardModel = (event) => {
    return {
        eyebrow: event.importedEventType ? event.importedEventType.toUpperCase() : null,
        identifier: event.thirdPartyIdentifier || null,
        verified: !!event.importedEventVerified,
        title: (event.name || '').replace(/_/g, ' '),
        subtitleParts: [],
        detailLeading: [],
        detailTrailing: [event.importedGender].filter(Boolean),
        formats: event.importedFormats || [],
        playerCount: event.registeredCount > 0 ? event.registeredCount : null,
        roleLabels: [UNIFIED_ROLE_PLAYER],
    };
};

const buildLeagueSessionCardModel = (event) => {
    return {
        eyebrow: event.leagueSessionType ? event.leagueSessionType.toUpperCase() : null,
        identifier: null,
        verified: false,
        title: event.name || '',
        subtitleParts: buildSubtitleParts(event.leagueSessionOrganizer, event.leagueSessionLocation, event.name),
        detailLeading: [],
        detailTrailing: [event.leagueSessionRatingRange, event.leagueSessionAgeRange, event.leagueSessionGender].filter(Boolean),
        formats: [],
        playerCount: null,
        roleLabels: [],
    };
};

const buildNativeRoleLabels = (isPlaying, isOrganizer) => {
    const roleLabels = [];
    if (isPlaying) {
        roleLabels.push(UNIFIED_ROLE_PLAYER);
    }
    if (isOrganizer) {
        roleLabels.push(UNIFIED_ROLE_PROVIDER);
    }
    return roleLabels;
};

const buildNativeDetailLeading = (event, isPaidHitEvent, prizeMoneyFirst) => {
    const fees = event.priceRangeShort || null;
    const prizeMoney = (event.hasPrizeMoney && event.prizeMoneyText && !isPaidHitEvent) ? event.prizeMoneyText : null;
    const ordered = prizeMoneyFirst ? [prizeMoney, fees] : [fees, prizeMoney];
    return ordered.filter(Boolean);
};

const buildNativeDetailTrailing = (event, isPaidHitEvent, isPickleBallEvent) => {
    const detailTrailing = [];
    if (event.gender && !isPaidHitEvent) {
        detailTrailing.push(event.gender);
    }
    if (event.utrRange) {
        detailTrailing.push(`${isPickleBallEvent ? ratingTypes.pickleball : ratingTypes.tennis} ${event.utrRange}`);
    }
    return detailTrailing;
};

const UNIFIED_FORMAT_ORDER = ['Singles', 'Doubles'];

const buildNativeFormats = (gameType, isPaidHitEvent) => {
    if (!gameType || isPaidHitEvent) {
        return [];
    }
    const formats = gameType.split('/').map(format => format.trim()).filter(Boolean);
    // The source string keeps whatever order the event was created with ("Doubles/Singles"), so two
    // cards on one profile can disagree. Imported events already arrive singles-first.
    return [
        ...UNIFIED_FORMAT_ORDER.filter(format => formats.includes(format)),
        ...formats.filter(format => !UNIFIED_FORMAT_ORDER.includes(format)),
    ];
};

const buildNativeCardModel = (event, context) => {
    const { isOrganizer, isPlaying, isPaidHitEvent, shouldHideClubName, locationCityStateZip, gameType, isPickleBallEvent, compactCard } = context;
    const roleLabels = buildNativeRoleLabels(isPlaying, isOrganizer);
    const detailLeading = buildNativeDetailLeading(event, isPaidHitEvent, compactCard);
    const detailTrailing = buildNativeDetailTrailing(event, isPaidHitEvent, isPickleBallEvent);
    const formats = buildNativeFormats(gameType, isPaidHitEvent);
    const showPlayerCount = (isOrganizer || event.playerCountVisibility) && event.registeredCount > 0;

    return {
        eyebrow: event.eventType?.label ? event.eventType.label.toUpperCase() : null,
        identifier: null,
        verified: event.utrType?.value === 'sanctioned',
        title: event.name || '',
        subtitleParts: buildSubtitleParts(shouldHideClubName ? null : event.club?.name, locationCityStateZip, event.name, shouldHideClubName ? null : event.club?.id),
        detailLeading,
        detailTrailing,
        formats,
        playerCount: showPlayerCount ? event.registeredCount : null,
        roleLabels,
    };
};

// The API's own eventDatesShort carries the year for past events and omits it for upcoming ones, so
// the card formats the date itself. Native events supply the calendar parts already resolved to the
// event's timezone; imported events and league sessions arrive with a year-bearing string built by
// getImportedEventDatesString.
const getUnifiedCardDateText = (event, context) => {
    const schedule = event.eventSchedule;
    if (schedule?.eventStartMonth && schedule?.eventStartDate) {
        return getEventDateString(schedule, true);
    }
    return schedule?.eventDatesString || context.eventDateString || null;
};

export const buildUnifiedEventCardModel = (event, context = {}) => {
    if (!event) {
        return null;
    }

    const dateText = getUnifiedCardDateText(event, context);

    let model;
    if (isImportedProfileEvent(event)) {
        model = buildImportedCardModel(event);
    } else if (isLeagueSessionProfileEvent(event)) {
        model = buildLeagueSessionCardModel(event);
    } else {
        model = buildNativeCardModel(event, context);
    }

    return { ...model, dateText, sportTypeId: event.sportTypeId };
};

export const getCombinedProfileEvents = (eventType, eventData = {}) => {
    const combinedEvents = [
        ...(eventData.tmsEvents || []),
        ...(eventData.leagueSessions || []),
        ...(eventData.importedEvents || []),
    ];
    const mappedEvents = combinedEvents.map(event => {
        if (event?.importedEventType) {
            return normalizeImportedProfileEvent(event);
        }
        if (event?.sessionId) {
            return normalizeLeagueSessionProfileEvent(event);
        }
        event.startDate = event?.eventSchedule?.eventStartUtc;
        return event;
    });

    if (eventType === EventScheduleTypes.RECENT) {
        mappedEvents.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    } else {
        mappedEvents.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    }

    return mappedEvents;
};

export const isSinglesFormat = (formatType) => {
    return formatType === divisionFormatTypeIds.SINGLES || formatType?.value === 'singles';
};

export const isDoublesFormat = (formatType) => {
    return formatType === divisionFormatTypeIds.DOUBLES || formatType === divisionFormatTypeIds.DINGLES || formatType?.value === 'doubles' || formatType?.value === 'dingles';
};

export const isDinglesFormat = (formatType) => {
    return formatType === divisionFormatTypeIds.DINGLES || formatType?.value === 'dingles';
};

export const isSinglesDivision = (division) => {
    return (division.divisionFormatType ? isSinglesFormat(division.divisionFormatType) : isSinglesFormat(division.divisionFormatTypeId));
};

export const isDoublesDivision = (division) => {
    return (division?.divisionFormatType ? isDoublesFormat(division?.divisionFormatType) : isDoublesFormat(division?.divisionFormatTypeId));
};

export const isDinglesDivision = (division) => {
    return (division.divisionFormatType ? isDinglesFormat(division.divisionFormatType) : isDinglesFormat(division.divisionFormatTypeId));
};

export const isPickleBallSportTypeEvent = (sportTypeId) => {
    return sportTypeId === sportTypeIds.PICKLEBALL;
};

export const isTennisSportTypeEvent = (sportTypeId) => {
    return sportTypeId === sportTypeIds.TENNIS;
};

export const renderDivisionPrice = (
    division,
    allowOffPlatformPayments,
    isSanctioned,
    workflowVersion,
    user,
    clubVerificationFee,
) => {
    const eventDivisionIsPlatformPaymentOrFreeResult = eventDivisionIsPlatformPaymentOrFree(division);
    const isNotPowerUser = user && !user.isPowered;
    const showPriceNonPowered = (eventDivisionIsPlatformPaymentOrFreeResult && !allowOffPlatformPayments);
    const isDivisionDingles = isDinglesDivision(division);
    const shouldShowNonPoweredPrice = isNotPowerUser &&
        isSanctioned &&
        workflowVersion > 1 &&
        clubVerificationFee > 0 &&
        showPriceNonPowered &&
        !isDivisionDingles;

    return shouldShowNonPoweredPrice ? division.priceNonPowered : division.price;
};

export const getAllowedFormatTypes = ({
    coreUI,
    isDinglesEnabled,
    isVerifiedEvent = false,
}) => {
    return coreUI.types.tms.division.formatTypes.filter((formatType) => {
        if (formatType.id === divisionFormatTypeIds.DINGLES) {
            return isDinglesEnabled && !isVerifiedEvent;
        }
        return true;
    });
};

export const getAllowedGenderTypes = (coreUI, isDoubleOrDingle = false) => {
    return coreUI.types.tms.genderTypes.filter((genderType) => {
        if (genderType.id === eventGenderTypeIds.MIXED) {
            return isDoubleOrDingle;
        }
        return true;
    });
};

// utrRange.pkbRating is the legacy /all-players field name; utrRange.pickleballRating
// is the v2 match-sheet endpoint's name for the same numeric value. Prefer the legacy
// name first so existing callers that pass /all-players-shaped objects keep working.
export const getAveragePickleballRating = (player1, player2) => {
    const p1rating = player1?.utrRange?.pkbRating ?? player1?.utrRange?.pickleballRating ?? 0;
    const p2rating = player2?.utrRange?.pkbRating ?? player2?.utrRange?.pickleballRating ?? 0;
    return (p1rating + p2rating) / 2;
};

export const getAveragePickleballRatingDisplayV2 = (player1, player2) => {
    const avg = Math.ceil(getAveragePickleballRating(player1, player2));
    if (avg < 1) {
        return pickleballRatingDisplayV2.P1;
    }
    if (avg >= 1 && avg < 2) {
        return pickleballRatingDisplayV2.P2;
    }
    if (avg >= 2 && avg < 3) {
        return pickleballRatingDisplayV2.P3;
    }
    if (avg >= 3 && avg < 4) {
        return pickleballRatingDisplayV2.P4;
    }
    if (avg >= 4) {
        return pickleballRatingDisplayV2.P5;
    }
};

export const getFindEventsSearchUrl = (search) => {
    let url = `/search?type=events&startDate=${moment(new Date()).format('MM/DD/YYYY')}`;
    if (search.geocoded) {
        url = `/search?type=events&startDate=${moment(new Date()).format('MM/DD/YYYY')}&distance=100mi&lat=${search.geocoded.lat}&lng=${search.geocoded.lng}&location=${search.geocoded.location}&locationInputValue=${search.geocoded.location}`;
    }
    return url;
};

export const getFindSportTypeEventsSearchUrl = (sportFilter) => {
    const { showTennisContent, showPickleballContent } = sportFilter;

    let sportType = '';

    if (showPickleballContent && showTennisContent) {
        sportType = 'tennis,pickleball';
    } else if (showTennisContent) {
        sportType = 'tennis';
    } else {
        sportType = 'pickleball';
    }

    return `/search?sportTypes=${sportType}&type=events`;
};

export const numRegExp = /^\d+(\.\d+)?$/;

export const isDinglesOrDoublesFormat = (formatType) => {
    return formatType === divisionFormatTypeIds.DOUBLES || formatType === divisionFormatTypeIds.DINGLES;
};

export const getDoublesOrDinglesIndex = (eventDivisions) => {
    return eventDivisions.findIndex(div => isDinglesOrDoublesFormat(parseInt(div.divisionFormatType)));
};

export const getUsapDefaultQuestionIndex = (eventQuestions) => {
    return eventQuestions.findIndex(val => val.type === eventQuestionTypeIds.THIRDPARTY_ID);
};

export const getAgeRestrictedDivisionIndex = (eventDivisions) => {
    return eventDivisions.findIndex(div => div.restrictAge);
};

export const getDivisionFormatAndPaymentType = (division, formatTypes, teamPaymentTypes) => {
    const labels = [];
    labels.push(getLabelByTypeId(formatTypes, division?.divisionFormatTypeId));

    if (division?.teamPaymentTypeId) {
        labels.push(getLabelByTypeId(teamPaymentTypes, division.teamPaymentTypeId));
    }
    return labels.join(' \u00B7 ');
};

export const displayFee = (currencySymbol, eventEntryFee, isPaid = false) => {
    const amount = `${currencySymbol}${eventEntryFee.toFixed(2)}`;
    return `${amount}${isPaid ? ' (PAID) ' : ''}`;
};

export const canUserRegisterForDivisionBasedOnGender = (division, user, genderTypes) => {
    const divisionGender = getLabelByTypeId(genderTypes, division.genderId);
    if (division.genderId === eventGenderTypeIds.ALL || division.genderId === eventGenderTypeIds.MIXED) {
        return true;
    }

    return divisionGender?.toLowerCase() === user.gender?.toLowerCase();
};

export const getPartnerGenderToSearch = (division, user, genderTypes) => {
    const userGenderId = genderTypes.find((gT) => {
        return getLabelByTypeId(genderTypes, gT.id)?.toLowerCase() === user.gender?.toLowerCase();
    })?.id;

    if (division.genderId === eventGenderTypeIds.MIXED) {
        if (userGenderId === eventGenderTypeIds.MALE) {
            return getLabelByTypeId(genderTypes, eventGenderTypeIds.FEMALE);
        }

        if (userGenderId === eventGenderTypeIds.FEMALE) {
            return getLabelByTypeId(genderTypes, eventGenderTypeIds.MALE);
        }
    }

    if (division.genderId === eventGenderTypeIds.ALL) {
        return null;
    }

    return getLabelByTypeId(genderTypes, division.genderId);
};

export const getPartnerSearchParams = (division, user, genderTypes) => {
    const searchParams = {};
    const gender = getPartnerGenderToSearch(division, user, genderTypes);
    if (gender) {
        searchParams.gender = gender;
    }

    return searchParams;
};

export const validateUserRegisteredNonEntryFeeDivisions = (nonEntryFeeDivisions, eventData) => {
    let isRegistered = false;
    if (eventData?.eventDivisions?.length > 0 && eventData?.currentMember?.registeredDivisionIds?.length > 0) {
        isRegistered = nonEntryFeeDivisions.some(div => eventData?.currentMember?.registeredDivisions?.some(regDiv => regDiv.id === div.id && regDiv.registrationStatusTypeId === registrationStatusTypeIds.REGISTERED));
    }
    return isRegistered;
};

export const partnerDetailsToSearchPlayer = (partnerDetails) => ({
    ...partnerDetails,
    id: partnerDetails.playerId,
    profileId: partnerDetails.playerId,
});

export const getInvitedByPlayer = (invitedDivision) => {
    const partner = invitedDivision?.partner;
    if (!partner) {
        return undefined;
    }

    return partnerDetailsToSearchPlayer(partner);
};

export const getInvitedByPlayerName = (invitedDivision) => {
    const invitedByPlayer = getInvitedByPlayer(invitedDivision);
    if (invitedByPlayer?.firstName && invitedByPlayer?.lastName) {
        return `${invitedByPlayer.firstName} ${invitedByPlayer.lastName}`;
    }

    return null;
};

export const hasPlayerInvitation = (playerDivision) => {
    const { registrationGroupStatus, registrationStatusTypeId, paymentStatusTypeId, divisionRegistrationTypeId } = playerDivision || {};

    return !divisionRegistrationTypeId &&
        paymentStatusTypeId === paymentStatusTypeIds.WAITING &&
        registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
        registrationGroupStatus === registrationGroupStatusTypeIds.PENDING &&
        playerDivision;
};

export const hasPlayerInvitationAndRegDateOpen = (playerDivision, eventDivision) => {
    return hasPlayerInvitation(playerDivision) && !isDivisionCustomRegistrationDateClosed(eventDivision);
};

export const hasAdminAddPaymentRequiredInvitation = (playerDivision) => {
    const { registrationGroupStatus, registrationStatusTypeId, paymentStatusTypeId, divisionRegistrationTypeId } = playerDivision || {};

    return divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED &&
        paymentStatusTypeId === paymentStatusTypeIds.WAITING &&
        registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
        (
            registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER ||
            registrationGroupStatus === registrationGroupStatusTypeIds.PENDING
        );
};

export const getEventDivisionFromPlayerDivision = (event) => (playerDivision) => {
    const division = event.eventDivisions
        .find((div) => div.id === playerDivision.id);
    if (division) {
        division.partners = playerDivision.partners;
    }

    return division;
};

export const getTeamPaymentTypeOfDivision = (division, teamPaymentTypes) => {
    const isDoubles = isDoublesDivision(division);
    if (isDoubles) {
        return getLabelByTypeId(teamPaymentTypes, division.teamPaymentTypeId);
    }
    // support for change partner data model
    if (division.canPaySeparately) {
        return 'Pay separately';
    }

    return null;
};

export const convertPlayerTOSearchPlayerObject = (player) => {
    const displayName = `${player.firstName} ${player.lastName}`;
    return {
        ...player,
        id: player.playerId,
        displayName,
    };
};

export const isDivisionCompletelyRegistered = (division, event) => {
    const currentMemberDivision = event.currentMember.registeredDivisions?.find(div => div.id === division.id);
    const isInvitedOnlyToDivision = hasPlayerInvitationAndRegDateOpen(currentMemberDivision, division) || hasAdminAddPaymentRequiredInvitation(currentMemberDivision);

    return !!currentMemberDivision && !isInvitedOnlyToDivision;
};

export const isDivisionCustomRegistrationDateClosed = (division, endDateOnly) => {
    if (endDateOnly) {
        return (
            (division.registrationEndUtc && moment().isAfter(moment.utc(division.registrationEndUtc))) ||
            division.closeRegistration
        );
    }
    return (
        (division.registrationStartUtc && moment().isBefore(moment.utc(division.registrationStartUtc))) ||
        (division.registrationEndUtc && moment().isAfter(moment.utc(division.registrationEndUtc))) ||
        division.closeRegistration
    );
};

export const doesUserHaveEligibleDivisions = (event, user) => {
    if (event?.eventDivisions?.length) {
        if (event?.eventDivisions?.length === 1 && event.eventDivisions[0]?.isEventEntryFeeDivision === true) {
            return false;
        }
        return event.eventDivisions.some((div) => {
            const userIsRegisteredToDivision = isDivisionCompletelyRegistered(div, event);
            const userIsEligible = isUserEligibleForDivision(div, user, event.eventSchedule?.eventStartUtc, event.sportTypeId, event);
            return !userIsRegisteredToDivision && userIsEligible && !div.isEventEntryFeeDivision;
        });
    }
    return false;
};

export const canAnswerPartnerEventQuestions = (division, event) => {
    const isPaySeparatelyDivision = division?.teamPaymentTypeId === teamPaymentTypeIds.PAY_SEPARATELY;

    if (isPaySeparatelyDivision) {
        const eventEntryFee = event?.entryFee || 0;
        const divisionFee = division?.entryFee || 0;

        return !(eventEntryFee > 0 || divisionFee > 0);
    }

    return true;
};

// show Complete Registration button on event cards
export const showCompleteRegistration = (event, playerId) => {
    const isRegistrationClosed = event?.eventState?.registrationState?.id === registrationStateTypeIds.REGISTRATION_CLOSED;
    const isEventCompleted = event?.eventState?.id === eventStateTypeIds.COMPLETED;
    const adminAddedPaymentRequiredDivisionIds = [...new Set(event?.divisions?.filter(div => {
        return (div.registrants || [{}]).find(reg => {
            return reg.id === playerId && reg.divisionRegistrationTypeId === divisionRegistrationTypeIds.ADMIN_ADD_PAYMENT_REQUIRED &&
                reg.paymentStatusTypeId === paymentStatusTypeIds.WAITING &&
                reg.registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
                (
                    reg.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER ||
                    reg.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING
                );
        });
    }).map(division => division.id))];
    const canByPassRegistrationClosedForAdminInvitation = isRegistrationClosed && !isEventCompleted && !!adminAddedPaymentRequiredDivisionIds?.length;

    return canByPassRegistrationClosedForAdminInvitation || (!isRegistrationClosed && !!(event.divisions || [{}]).find(div => {
        return (div.registrants || [{}]).find(reg => {
            return reg.id === playerId &&
                reg.paymentStatusTypeId === paymentStatusTypeIds.WAITING &&
                reg.registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
                reg.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING;
        });
    }));
};

export const getRegisteredDivisionsWithNeedsPartner = (registeredDivisions = []) => {
    return registeredDivisions.filter(regDivObj => regDivObj.registrationGroupStatus === registrationGroupStatusTypeIds.NEED_PARTNER && !regDivObj.pendingPartnerFromNeedsPartnerList);
};

export const getRegisteredCountDisplay = (registeredCount, divisionFormatTypeId, isPaidHitEvent, isPaidHitterRegistered) => {
    const isDoubles = divisionFormatTypeId === divisionFormatTypeIds.DOUBLES || divisionFormatTypeId === divisionFormatTypeIds.DINGLES;
    const registeredPlayersCount = isPaidHitEvent && isPaidHitterRegistered ? registeredCount - 1 : registeredCount || 0;
    if (isDoubles) {
        return registeredCount;
    }
    return registeredPlayersCount;
};

export const showCompleteRegistrationButton = (registeredDivisions, eventDivisions) => registeredDivisions.find(regDivObj => {
    const eventDivision = eventDivisions.find(div => div.id === regDivObj.id);
    return regDivObj.paymentStatusTypeId === paymentStatusTypeIds.WAITING &&
        regDivObj.registrationStatusTypeId === registrationStatusTypeIds.CREATED &&
        regDivObj.registrationGroupStatus === registrationGroupStatusTypeIds.PENDING &&
        !isDivisionCustomRegistrationDateClosed(eventDivision);
}) !== undefined;

export const purchaseTypeIds = {
    EVENT_REGISTRATION: 1,
};

export const discountTypeIds = {
    PERCENTAGE: 1,
    FLAT_AMMOUNT: 2,
};

export const isBucketUnderMaxPlayerLimit = (bucket, selectedDivision) => {
    const isDivisionSingles = isSinglesFormat(selectedDivision.divisionFormatTypeId);
    const maxPlayers = (isDivisionSingles ? selectedDivision?.maxPlayers : selectedDivision?.maxPlayers / 2) || noMaximumLabelValue;
    return bucket.length < maxPlayers;
};

export const getRefundDataForEvent = async (apiInstance, eventId, refundPostBodyRequest, isSelfWithdraw) => {
    let totalDiscountsPlayerReceived = 0;
    const withdrawFunction = isSelfWithdraw ? checkoutEventRegistrationApi.getSelfWithdrawRefundData : checkoutEventRegistrationApi.getPromoCodeRefundData;
    const { data } = await withdrawFunction(apiInstance, eventId, refundPostBodyRequest);
    const {
        checkouts,
        totalRefundAmount,
        verificationFeeRefundTotal,
        serviceFeeRefundTotal = 0,
        smsFeeRefundTotal = 0,
        organizerRefundTotal,
        percentageFeeRefundTotal,
        playerEventEntryFeeRefundTotal = 0,
        divisionFeeRefundedTotal = 0,
        feesForfeitedTotal = 0,
        totalAmountPaid = 0,
        formattedCancellationFeeTotal,
        cancellationFeeTotal,
    } = data;
    totalDiscountsPlayerReceived = checkouts.reduce((total, co) => total + co.originalDiscount, 0);
    const promoCodesSet = new Set();
    checkouts
        .map(checkout => checkout.promoCode)
        .filter(code => code)
        .forEach(code => promoCodesSet.add(code));

    const promoCodesArray = Array.from(promoCodesSet);
    const promoCodesUsed = promoCodesArray.length === 1
        ? promoCodesArray[0]
        : `${promoCodesArray.slice(0, -1).join(', ')} & ${promoCodesArray.slice(-1)}`;

    const originalSubTotal = checkouts.reduce((total, co) => total + co.originalSubtotal, 0);

    return {
        totalDiscountsPlayerReceived,
        promoCodesUsed,
        totalRefundAmount,
        verificationFeeRefundTotal,
        percentageFeeRefundTotal,
        playerEventEntryFeeRefundTotal,
        organizerRefundTotal,
        serviceFeeRefundTotal,
        smsFeeRefundTotal,
        originalSubTotal,
        divisionFeeRefundedTotal,
        feesForfeitedTotal,
        totalAmountPaid,
        formattedCancellationFeeTotal,
        cancellationFeeTotal,
    };
};
export const populateDivisionTypesFilterOptions = (divisions) => {
    let singlesCount = 0;
    let doublesCount = 0;
    let dinglesCount = 0;

    const divisionTypes = [];
    divisions.forEach(div => {
        if (div.divisionFormatTypeId === divisionFormatTypeIds.SINGLES) {
            singlesCount += 1;
        }
        if (div.divisionFormatTypeId === divisionFormatTypeIds.DOUBLES) {
            doublesCount += 1;
        }
        if (div.divisionFormatTypeId === divisionFormatTypeIds.DINGLES) {
            dinglesCount += 1;
        }
    });
    if (singlesCount) {
        divisionTypes.push({ label: `Singles (${singlesCount})`, value: divisionFormatTypeIds.SINGLES });
    }
    if (doublesCount) {
        divisionTypes.push({ label: `Doubles (${doublesCount})`, value: divisionFormatTypeIds.DOUBLES });
    }
    if (dinglesCount) {
        divisionTypes.push({ label: `Dingles (${dinglesCount})`, value: divisionFormatTypeIds.DINGLES });
    }
    return divisionTypes;
};

export const getDivisionStats = (divisions) => {
    let hasDivisionDate;
    let hasRatingLimit;
    let hasAgeLimit;

    let maleCount = 0;
    let femaleCount = 0;
    let mixedCount = 0;
    let coedCount = 0;

    divisions.forEach(div => {
        if (div.specificDivisionDateUtc) {
            hasDivisionDate = true;
        }
        if (div.minUtr !== 1 || div.maxUtr !== 10) {
            hasRatingLimit = true;
        }
        if (div.minAge !== 5 || div.maxAge !== 100) {
            hasAgeLimit = true;
        }
        if (div.genderId === eventGenderTypeIds.ALL) {
            coedCount += 1;
        }
        if (div.genderId === eventGenderTypeIds.MALE) {
            maleCount += 1;
        }
        if (div.genderId === eventGenderTypeIds.FEMALE) {
            femaleCount += 1;
        }
        if (div.genderId === eventGenderTypeIds.MIXED) {
            mixedCount += 1;
        }
    });

    return {
        hasDivisionDate,
        hasRatingLimit,
        hasAgeLimit,
        maleCount,
        femaleCount,
        mixedCount,
        coedCount,
    };
};

export const lastDayOfYear = moment().endOf('year').utc();

export const isUserEligibleForDivision = (division, user, eventStartUtc, sportTypeId, event) => {
    const isUserEligibleForEvent = event?.currentMember?.isEligibleForEvent;
    const isEligibleListEnabled = event?.preRegAndEligibleListEnabled;
    let isUserEligible = true;
    if (!user) {
        return true;
    }
    const birthDate = user?.claimedPlayer?.birthDate || user.birthdate;
    const userAge = getUserAge(birthDate, division.restrictAgeByDateType, eventStartUtc);
    if (division.restrictByRating) {
        const userRating = getUserRating(user?.claimedPlayer, division.divisionFormatTypeId, sportTypeId);
        if (userRating && division.minUtr && division.maxUtr) {
            isUserEligible = userRating >= division.minUtr && userRating <= division.maxUtr;
        }
    }
    if (division.restrictAge) {
        if (division.minAge && division.minAge > userAge) {
            isUserEligible = false;
        }
        if (division.maxAge && division.maxAge < userAge) {
            isUserEligible = false;
        }
    }
    if (division.genderId === eventGenderTypeIds.MALE && user.gender === LeagueGenderValues.FEMALE) {
        isUserEligible = false;
    }
    if (division.genderId === eventGenderTypeIds.FEMALE && user.gender === LeagueGenderValues.MALE) {
        isUserEligible = false;
    }
    if (isEligibleListEnabled && !isUserEligibleForEvent && !division.excludeFromEligibleListRestrictions) {
        isUserEligible = false;
    }
    return isUserEligible;
};

export const getUserAge = (birthdate, restrictAgeByDateType, eventStartUtc) => {
    let userAge = 0;
    let dateToCompare;
    if (parseInt(restrictAgeByDateType) === RestrictAgeByDateTypeIds.EVENTSTARTDATE) {
        dateToCompare = moment.utc(eventStartUtc);
    }
    if (parseInt(restrictAgeByDateType) === RestrictAgeByDateTypeIds.ENDOFYEAR) {
        dateToCompare = lastDayOfYear;
    }
    if (birthdate) {
        if (dateToCompare) {
            userAge = dateToCompare.diff(birthdate, 'years');
        } else {
            userAge = moment().diff(birthdate, 'years');
        }
    }
    return userAge;
};

export const getUserRating = (user, divisionFormatTypeId, sportTypeId) => {
    if (!user) {
        return null;
    }
    const isSingles = parseInt(divisionFormatTypeId) === divisionFormatTypeIds.SINGLES;
    if (parseInt(sportTypeId) === sportTypeIds.PICKLEBALL) {
        if (isSingles) {
            return user.singlesPkbRating != null ? user.singlesPkbRating : user.unverifiedSinglesPkbRating;
        }
        return user.doublesPkbRating != null ? user.doublesPkbRating : user.unverifiedDoublesPkbRating;
    }
    if (parseInt(sportTypeId) === sportTypeIds.TENNIS) {
        if (isSingles) {
            return user.ratingSingles != null ? user.ratingSingles : user.myUtrSingles;
        }
        return user.ratingDoubles != null ? user.ratingDoubles : user.myUtrDoubles;
    }
    return undefined;
};

export const getFeedOutRoundOptions = (drawSize, formatTypeId) => {
    let label = (formatTypeId === divisionFormatTypeIds.DOUBLES || formatTypeId === divisionFormatTypeIds.DINGLES) ? 'teams' : 'players';
    if (!formatTypeId) {
        label = 'players / teams';
    }
    const roundSelectOptions = [];
    let roundNumber = 1;
    let playersToCutOff = drawSize;
    while (playersToCutOff >= 2) {
        roundSelectOptions.push({
            id: roundNumber,
            label: `Round ${roundNumber} (${playersToCutOff} ${label}, ${playersToCutOff / 2} qualify)`,
        });
        playersToCutOff /= 2;
        roundNumber += 1;
    }
    return roundSelectOptions;
};

export const convertScoringOverrideMainCategoryToPro = (scoringTypeOverrides) => {
    return scoringTypeOverrides.map((stOverride) => {
        return {
            ...stOverride,
            categoryTypeId: stOverride.categoryTypeId === drawCategoryTypeIds.MAIN ? drawCategoryTypeIds.PRO_DRAW : stOverride.categoryTypeId,
        };
    });
};

export const convertScoringOverrideProCategoryToMain = (scoringTypeOverrides) => {
    return scoringTypeOverrides.map((stOverride) => {
        return {
            ...stOverride,
            categoryTypeId: stOverride.categoryTypeId === drawCategoryTypeIds.PRO_DRAW ? drawCategoryTypeIds.MAIN : stOverride.categoryTypeId,
        };
    });
};

export const updateEventDrawScoringTypeOverrides = (values, overridesList = scoringTypeOverrides) => {
    const scoringTypeAndAllottedMatchTimeOverrides = [];

    overridesList.forEach((stOverride) => {
        const overrideData = values[stOverride.id];

        if (overrideData) {
            if (overrideData.override) {
                scoringTypeAndAllottedMatchTimeOverrides.push({
                    scoringTypeId: overrideData.scoringTypeId,
                    allottedMatchTime: overrideData.allottedMatchTime,
                    categoryTypeId: stOverride.categoryTypeId,
                    isFinalRound: stOverride.isFinalRound,
                });
            }

            delete values[stOverride.id];
        }
    });

    const updatedOverrides = parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS
        ? convertScoringOverrideMainCategoryToPro(scoringTypeAndAllottedMatchTimeOverrides)
        : scoringTypeAndAllottedMatchTimeOverrides;

    return {
        ...values,
        scoringTypeAndAllottedMatchTimeOverrides: updatedOverrides,
    };
};

export const getFormValuesFromEventDrawScoringTypeOverrides = (scoringTypeAndAllottedMatchTimeOverrides, eliminationTypeId) => {
    let formValues = {};

    if (scoringTypeAndAllottedMatchTimeOverrides?.length > 0) {
        const currentEliminationTypeOverrides = scoringTypeOverrides
            .filter((stOverride) => stOverride.drawEliminationTypeId === parseInt(eliminationTypeId));

        scoringTypeAndAllottedMatchTimeOverrides.forEach((override) => {
            const validOverride = currentEliminationTypeOverrides.find((sTOverride) => {
                if (sTOverride.categoryTypeId === override.categoryTypeId) {
                    if ('isFinalRound' in sTOverride) {
                        return sTOverride.isFinalRound === override.isFinalRound;
                    }
                    return true;
                }
                return false;
            });

            if (validOverride) {
                formValues = {
                    ...formValues,
                    [validOverride.id]: {
                        allottedMatchTime: override.allottedMatchTime,
                        scoringTypeId: override.scoringTypeId,
                        override: true,
                    },
                };
            }
        });
    }

    return formValues;
};

export const getDefaultAllottedTimeForScoringType = (scoringTypeId, eventAllottedMatchTimes = [], appLevelAllottedMatchTimesForScoringType = []) => {
    if (eventAllottedMatchTimes?.length > 0) {
        const allottedMatchTimeObject = eventAllottedMatchTimes.find((matchAllottedTime) => {
            return parseInt(matchAllottedTime.scoringTypeId) === parseInt(scoringTypeId);
        });

        if (allottedMatchTimeObject) {
            return allottedMatchTimeObject.allottedMatchTime;
        }
    }

    if (appLevelAllottedMatchTimesForScoringType?.length > 0) {
        const allottedMatchTimeObject = appLevelAllottedMatchTimesForScoringType.find((scoringType) => {
            return parseInt(scoringType.id) === parseInt(scoringTypeId);
        });

        if (allottedMatchTimeObject) {
            return allottedMatchTimeObject.allottedMatchTime;
        }
    }

    return null;
};

export const getRegistrationTotal = ({ total, serviceFee }) => {
    const updatedServiceFee = total === 0 ? 0 : serviceFee || 0;
    const updatedTotal = total + updatedServiceFee;

    return {
        updatedTotal,
        updatedServiceFee,
    };
};

export const getScheduleTypeValues = ({
    sportTypeId,
    drawScheduleTypes,
    scheduleTypeDescriptions,
    checkStatsigGate,
}) => {
    return drawScheduleTypes
        .map((scheduleType) => {
            const descriptionObject = scheduleTypeDescriptions.find((sTDesc) => sTDesc.id === scheduleType.id);

            return {
                ...scheduleType,
                ...descriptionObject,
            };
        })
        .filter((scheduleType) => scheduleType?.enabledSportTypeIds?.includes(sportTypeId))
        .filter((scheduleType) => {
            if (scheduleType.isFeatureFlagged) {
                return checkStatsigGate(scheduleType.featureFlagName);
            }

            return true;
        });
};

export const getFacilityAndCourt = (locations, courtId, facilityId) => {
    let facility;
    let court;

    if (courtId) {
        locations.forEach((location) => {
            location.facilities.forEach((fac) => {
                const facilityCourt = fac.courts.find((court) => {
                    return court.id === courtId;
                });

                if (facilityCourt) {
                    facility = fac;
                    court = facilityCourt;
                }
            });
        });
    } else if (facilityId) {
        facility = getFacility(locations, facilityId);
    }

    return {
        facility,
        court,
    };
};

export const getFacility = (locations, facilityId) => {
    let facility;

    locations.forEach((location) => {
        if (facility) {
            return;
        }

        facility = location.facilities.find((fac) => {
            return fac.id === facilityId;
        });
    });

    return facility;
};

export const getPlayers = (position, playersDictByMemberId, teamsDict) => {
    let players = [];

    if (position.typeId === drawPositionTypeIds.PLAYER) {
        players = [
            playersDictByMemberId[position.memberId],
        ];
    } else if (position.typeId === drawPositionTypeIds.TEAM) {
        players = teamsDict[position.teamId].members.map((memberId) => playersDictByMemberId[memberId]);
    } else if (position.typeId === drawPositionTypeIds.BYE) {
        players = [{
            id: position.id,
            firstName: 'BYE',
            lastName: '',
            isBye: true,
        }];
    } else {
        players = [{
            id: position.id,
            firstName: 'TBD',
            lastName: '',
            isTbd: true,
        }];
    }

    return players;
};

export const isEventRunning = (eventSchedule) => {
    if (eventSchedule) {
        const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule.timeZone?.value);
        const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value);
        const currentDate = moment.utc().tz(eventSchedule.timeZone?.value);

        return currentDate.isBetween(eventStartDate, eventEndDate, 'second', '[]');
    }

    return false;
};

export const getDrawEliminationTypeId = (values) => {
    if (parseInt(values.eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS) {
        return values.parentDrawEliminationTypeId;
    }

    return values.eliminationTypeId;
};

export const getEliminationTypeIdFromDrawData = (drawData) => {
    if (drawData?.isProDraw) {
        return drawEliminationTypeIds.PRO_DRAWS;
    }

    return drawData.eliminationTypeId;
};

export const getParentDrawEliminationTypeIdFromDrawData = (drawData) => {
    if (drawData?.isProDraw) {
        return drawData.eliminationTypeId;
    }

    return null;
};

export const getUserAgeAtEventStart = (birthdate, eventStartUtc) => {
    const dateToCompare = moment.utc(eventStartUtc);
    if (birthdate && dateToCompare) {
        return dateToCompare.diff(birthdate, 'years');
    }
};

export const isPlayerCheckedIntoDraw = (player, drawId) => {
    return player?.checkInDraws?.find((checkInDraw) => {
        return checkInDraw.id === drawId;
    })?.checkedIn;
};

export const getMinDateAllowedForPreRegInviteExtend = (
    timeZone,
    totalPages = 1,
    allPlayersInRangeSelected = false,
    expirationDateOptions = [],
    invitesList = [],
) => {
    const today = moment().tz(timeZone?.value).startOf('day');
    if (allPlayersInRangeSelected && totalPages > 1) {
        // use maximum date from filter dates
        if (expirationDateOptions.length > 0) {
            const maxExpiryDate = expirationDateOptions.reduce((maxDate, date) => {
                const expiryDate = moment(date?.label);
                const expiryDateInTimeZone = moment().tz(timeZone?.value).set({
                    year: expiryDate.year(),
                    month: expiryDate.month(),
                    date: expiryDate.date(),
                    hour: 0,
                    minute: 0,
                    second: 0,
                });

                if (maxDate === null && expiryDateInTimeZone.isValid()) {
                    return expiryDateInTimeZone;
                }

                if (maxDate !== null && expiryDateInTimeZone.isValid() && expiryDateInTimeZone.isAfter(maxDate)) {
                    return expiryDateInTimeZone;
                }

                return maxDate;
            }, null);

            if (maxExpiryDate?.isValid() && maxExpiryDate.isAfter(today)) {
                return maxExpiryDate;
            }

            return today;
        }
    }

    if (invitesList?.length > 0) {
        const maxExpiryDateForSelectedPlayers = invitesList
            .filter(player => player.isSelected)
            .reduce((maxDate, player) => {
                if (player.expirationDate) {
                    const playerExpiryDate = moment.utc(player.expirationDate).tz(timeZone?.value);
                    if (playerExpiryDate.isValid() && playerExpiryDate.isAfter(maxDate)) {
                        return playerExpiryDate;
                    }
                }
                return maxDate;
            }, today);

        return maxExpiryDateForSelectedPlayers.isValid() ? maxExpiryDateForSelectedPlayers : today;
    }

    return today;
};

export const getEventDurationString = (eventStartDate, eventEndDate) => {
    const startDate = moment(eventStartDate);
    const endDate = moment(eventEndDate);

    if (!startDate.isValid() || !endDate.isValid()) {
        return '';
    }

    if (startDate.year() === endDate.year()) {
        if (startDate.month() === endDate.month()) {
            if (startDate.date() === endDate.date()) {
                return startDate.format('MMM D, YYYY');
            }

            return `${startDate.format('MMM D')} - ${endDate.format('D, YYYY')}`;
        }

        return `${startDate.format('MMM D')} - ${endDate.format('MMM D, YYYY')}`;
    }

    return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
};

export const checkIfStatsigUpdateIsNeeded = (statsigEventData, statsigUser) => {
    const shouldUpdateStatsigUserSportType = statsigEventData?.sportTypeId &&
        (!statsigUser?.custom?.sportTypeId || statsigUser?.custom?.sportTypeId !== statsigEventData?.sportTypeId);
    const shouldUpdateStatsigUserClubId = statsigEventData?.clubId &&
        (!statsigUser?.custom?.clubId || statsigUser?.custom?.clubId !== statsigEventData?.clubId);

    return shouldUpdateStatsigUserSportType || shouldUpdateStatsigUserClubId;
};

export const areAllTeamMembersSelected = ({ players = [], selectedPlayers = [], selectedDivision, isDoubles }) => {
    const playersToMove = players?.filter(player => selectedPlayers?.includes(player?.memberId));
    return playersToMove?.every(player => {
        const playerDivisionObj = player?.divisions?.find(div => div?.id === selectedDivision?.id);
        const partnerId = playerDivisionObj?.partners?.[0];
        return !isDoubles || !partnerId || selectedPlayers?.includes(partnerId);
    });
};

export const areWithdrawnPlayersSelected = ({ players = [], selectedPlayers = [], selectedDivision, withdrawnPlayers = [] }) => {
    return selectedPlayers.some((selectedMemberId) => {
        return withdrawnPlayers.some((withdrawnPlayer) => {
            return withdrawnPlayer.playerId && players?.some((player) => player.memberId === selectedMemberId && player.playerId === withdrawnPlayer.playerId && withdrawnPlayer.divisions?.some((div) => div.id === selectedDivision.id));
        });
    });
};

export const rankList = (data) => {
    return (
        <ol className="mt8 ml-20">
            {data.map((item) => {
                if (Array.isArray(item)) {
                    return (
                        <ol key={item.join('-')} type="a" className="ml-20">
                            {item?.map((subItem) => {
                                if (Array.isArray(subItem)) {
                                    return (
                                        <ol key={subItem.join('-')} type="i" className="ml-20">
                                            {subItem?.map((s) => (
                                                <li key={s}>{s}</li>
                                            ))}
                                        </ol>
                                    );
                                }
                                return <li key={subItem}>{subItem}</li>;
                            })}
                        </ol>
                    );
                }
                return <li key={item}>{item}</li>;
            })}
        </ol>
    );
};

export const isClubOrganizer = (event) => {
    return event?.currentMember?.clubMemberRoleId === clubMemberRoleIds.OWNER || event?.currentMember?.clubMemberRoleId === clubMemberRoleIds.ADMIN;
};

export const getBucketPlayersBasedOnVisibility = (buckets, showPlayers, memberId) => {
    if (!showPlayers) {
        return buckets.map((bucket) => {
            bucket.players = bucket.players.filter((player) => {
                return player.memberId === memberId;
            });
            return bucket;
        });
    }
    return buckets;
};

export const shouldDisplayAllDivisionsFilterInPlayerList = (eventDivisions) => {
    const nonEntryFeeDivisions = eventDivisions?.filter(div => !div.isEventEntryFeeDivision) || [];

    const acceptanceDivisions = nonEntryFeeDivisions?.filter(div => div.isAcceptance);
    const nonAcceptanceDivisions = nonEntryFeeDivisions?.filter(div => !div.isAcceptance);

    if (nonAcceptanceDivisions.length > 0) {
        return true;
    }

    return acceptanceDivisions.length != 1;
};

export const getAcceptanceGridLayout = (isCurrentMemberAdmin, showAgeColumn, isPttEvent) => {
    return [
        ...(isCurrentMemberAdmin ? ['4fr'] : ['3fr']), // name
        ...(showAgeColumn ? ['1fr'] : []), // Age
        ...(['2fr', '2fr']), // rating
        ...(isPttEvent ? ['2fr'] : []), // ptt rank
        ...(isCurrentMemberAdmin ? ['2fr'] : []), // reg date
        ...(isCurrentMemberAdmin ? ['2fr'] : []), // status
        ...(isCurrentMemberAdmin ? ['2fr'] : []), // payment
    ];
};

const getDivisionsGridLayout = ({ isCurrentMemberAdmin, showDivisions, showRegStatus, showPartner, showDraws }) => [
    '5fr', // name
    ...(['3fr', '3fr']), // ratings
    // ...(isCurrentMemberAdmin ? ['2fr'] : []), // age
    ...(showDivisions ? ['5fr'] : []), // divisions
    ...(showDivisions && isCurrentMemberAdmin ? ['3fr'] : []), // reg date
    ...(showDivisions && showRegStatus ? ['3fr'] : []), // reg status
    ...(showDivisions && showPartner ? ['2fr'] : []), // reg.partner
    ...(showDivisions ? ['2fr'] : []), // reg.position
    ...(showDivisions && isCurrentMemberAdmin ? ['2fr'] : []), // reg.payment
    ...(showDraws ? ['5fr'] : []), // draws
    ...(showDraws ? ['2fr'] : []), // draw partner
    ...(showDraws ? ['2fr'] : []), // draw status
    ...(showDraws ? ['2fr'] : []), // player status
];

const getDrawsOnlyGridLayout = ({ showDraws }) => [
    '4fr', // name
    ...(['2fr', '2fr']), // ratings
    ...(showDraws ? ['4fr'] : []), // draws
    ...(showDraws ? ['2fr'] : []), // draw partner
    ...(showDraws ? ['2fr'] : []), // draw status
    ...(showDraws ? ['2fr'] : []), // player status
];

export const getAllDivisionsGridLayout = (params) => (params.showDivisions ? getDivisionsGridLayout(params) : getDrawsOnlyGridLayout(params));

export const getTimeSelectOptions = () => {
    const options = [];
    let i;
    let j;
    let t;
    let tText;
    for (i = 0; i < 24; i += 1) {
        for (j = 0; j < 4; j += 1) {
            const hourString24 = (`0${i}`).slice(-2);
            const minuteString = j === 0 ? '00' : j * 15;
            const amHourString12 = (`0${i === 0 ? 12 : i}`).slice(-2);
            const pmHourString12 = (`0${i % 12}`).slice(-2);
            const hourString12 = i < 13 ? amHourString12 : pmHourString12;
            const amPmString = i < 12 ? 'am' : 'pm';
            t = `${hourString24}:${minuteString}`;
            tText = `${hourString12}:${minuteString} ${amPmString}`;
            options.push({ value: t, title: tText });
        }
    }

    return options;
};

export const showUnder18 = (event) => (
    event?.data?.eventDivisions?.filter(d => d.isAcceptance).length === 1
);

export const hasQualifyingAcceptanceBucket = (event) => (
    !!event?.data?.eventDivisions?.some(div => (
        div.isAcceptance &&
        div.acceptanceBucketTypeIds?.includes(acceptanceBucketTypeSortKey.QUALIFYING)
    ))
);

export const getDivisionCountsView = (divisionCounts) => {
    const dc = divisionCounts?.data?.divisions?.[0] ?? {};
    const mainCountUnder18 = dc.u18?.main ?? 0;
    const qualifyingCountUnder18 = dc.u18?.qualifying ?? 0;
    const alternateCountUnder18 = dc.u18?.alternate ?? 0;
    return {
        mainCount: dc.main ?? 0,
        qualifyingCount: dc.qualifying ?? 0,
        alternateCount: dc.alternate ?? 0,
        mainCountUnder18,
        qualifyingCountUnder18,
        alternateCountUnder18,
        totalUnder18: mainCountUnder18 + qualifyingCountUnder18 + alternateCountUnder18,
    };
};

export const shouldShowDivisionCounts = (isUnder18, divisionCounts) => (
    !!isUnder18 &&
    !divisionCounts?.loading &&
    !divisionCounts?.error &&
    divisionCounts?.data != null
);

export const canDeleteEvent = (event) => (
    event?.data?.registeredCount === 0 &&
    (event?.data?.withdrawnRegistrationsCount ?? 0) === 0
);
